// Administration:

// {{{ Admin.Screen

GW.define('Admin.Screen', 'Main.Screen', {
	title: _('Administrace'),
	titleIcon: 'settings',

	getSidebarMenu() {
		let is_superadmin = Application.isSuperadminUser();

		return this.getSidebarMenuItems().map(s => {
			return {
				':skip': !is_superadmin && s.superadmin_only,
				nodeName: 'a',
				className: 'item',
				href: APP_BASE + 'admin/' + s.id,
				children: [Utils.useIcon(s.icon), {
					textContent: s.title,
				}],
			};
		});
	},

	/*
	 * PLUGIN: extension point for adding more admin sidebar menu items
	 */
	getSidebarMenuItems() {
		return Admin.screens;
	},

	'after:initComponent'() {
		this.el.gwCreateChild({
			className: 'admin-body',
			children: [this.sidebarEl, this.bodyEl],
		});

		this.bodyEl.classList.remove('page-centered-container');
	},
});

// }}}

// Users:

// {{{ Admin.UsersScreen

GW.define('Admin.UsersScreen', 'Admin.Screen', {
	'after:initComponent'() {
		this.bodyEl.gwCreateChild({
			ref: 'editor',
			xtype: 'Widgets.EntityListEditor',
			title: _('Uživatelé'),
			entityApiPrefix: 'users',
			createText: _('Nový uživatel'),
			selectText: _('Vyberte uživatele ze seznamu'),
			editText: _('Upravit uživatele'),
			deleteText: i => _('Opravdu smazat uživatele {name}?').replace('{name}', i.name),
			defaultFilterOption: 'active',
			renderDetail: null,

			allowAction(item, action) {
				let is_pbx_admin = !!Application.session.user.pbxadmin;

				if (action == 'deactivate' && item.superadmin) {
					return false;
				}

				switch (action) {
				case 'password':
				case 'deactivate':
				case 'edit':
					return item.pbxadmin ? is_pbx_admin : true;
				}

				return action != 'delete';
			},

			filterListItem(i, match, {filter}) {
				if (filter == 'active' && i.suspended) {
					return false;
				}

				if (filter == 'suspended' && !i.suspended) {
					return false;
				}

				return match([i.name, i.default_extension || '', i.external_exten || '', i.email].join(' '));
			},

			getFilterOptions() {
				return [{
					value: 'all',
					text: _('Všichni'),
				}, {
					value: 'active',
					text: _('Aktivní'),
				}, {
					value: 'suspended',
					text: _c('Neaktivní', 'users'),
				}];
			},

			getExtraToolbarItems() {
				return ImportExportToolbarItems({
					title: this.title,
					
					columns: [{
						id: 'name',
						name: _('Jméno'),
						width: 20,
						getError(v) {
							if (!v) {
								return _('Nesmí být prázdné');
							}
						},
					}, {
						id: 'email',
						name: _('E-mail'),
						width: 20,
						getError(v) {
							if (!v) {
								return _('Nesmí být prázdné');
							}
						},
					}, {
						id: 'default_extension',
						width: 10,
						name: _('Interní linka'),
					}, {
						id: 'external_exten',
						width: 20,
						name: _('Externí linka'),
					}, {
						id: 'password',
						width: 20,
						name: _('Heslo pro přihlášení (Prázdné = beze změny)'),
						getError(v, item, extra_data, existing_item) {
							if (!v && !existing_item) {
								return _('Heslo musí být zadáno, pokud uživatel ještě neexistuje');
							}
						},
					}],

					row_key: r => String(r.email).toLowerCase(),
					item_key: i => i.email,

					getItems: async () => {
						return await this.entityGetAll();
					},

					createItem: async (row) => {
						let data = {
							email: row.email,
							name: row.name,
							password: row.password,
							default_extension: row.default_extension,
							external_exten: row.external_exten,
						};

						await this.entityCreate(data);
					},

					updateItem: async (existing, row) => {
						let data = {
							...existing,
							id: undefined,
							email: row.email,
							name: row.name,
							password: row.password || undefined, // do not change the password if empty in the data
							default_extension: row.default_extension,
							external_exten: row.external_exten,
						};

						await this.entityUpdate(existing, data);
					},

					done: () => {
						this.reload();
					},
				});
			},

			getExtraDetailActions(item) {
				if (item.external) {
					return [];
				}

				return [{
					hidden: !this.allowAction(item, 'password'),
					secondary: true,
					text: _('Změnit heslo'),
					handle: () => this.changePassword(item),
				}, {
					hidden: !this.allowAction(item, item.suspended ? 'activate' : 'deactivate'),
					secondary: true,
					text: item.suspended ? _('Aktivovat') : _('Deaktivovat'),
					handle: () => this.setUserSuspended(item, !item.suspended),
				}];
			},

			getCreateFormFields() {
				return [{
					xtype: 'TextField',
					name: 'name',
					label: _('Jméno'),
				}, {
					xtype: 'TextField',
					name: 'email',
					type: 'email',
					label: _('E-mail'),
					autocomplete: "off",
				}, {
					xtype: 'PasswordTextField',
					name: 'password',
					label: _('Heslo'),
					autocomplete: "new-password",
				}, {
					xtype: 'TextField',
					name: 'default_extension',
					label: _('Interní linka'),
					optional: true,
					pattern: '^[0-9]{1,9}$',
				}, {
					xtype: 'TextField',
					name: 'external_exten',
					label: _('Externí linka'),
					pattern: '^\\+?[0-9]{1,15}$',
					optional: true,
				}];
			},

			getEditFormFields(item) {
				return [{
					xtype: 'TextField',
					name: 'name',
					label: _('Jméno'),
					readOnly: item.external,
					disabled: item.external,
				}, {
					xtype: 'TextField',
					name: 'email',
					type: 'email',
					label: _('E-mail'),
					readOnly: item.external,
					disabled: item.external,
				}, {
					xtype: 'TextField',
					name: 'default_extension',
					label: _('Interní linka'),
					pattern: '^[0-9]{1,9}$',
					optional: true,
					visibleWhen: data => !data.superadmin,
				}, {
					xtype: 'TextField',
					name: 'external_exten',
					label: _('Externí linka'),
					pattern: '^\\+?[0-9]{1,15}$',
					optional: true,
					visibleWhen: data => !data.superadmin,
				}, {
					xtype: 'AckCheckboxField',
					name: 'using_timecards',
					shortText: _('Píchačky'),
					optional: true,
				},{
					xtype: 'AckCheckboxField',
					name: 'superadmin',
					shortText: _('Superadmin'),
					optional: true,
				}, {
					':skip': !Application.session.user.pbxadmin,
					xtype: 'AckCheckboxField',
					name: 'pbxadmin',
					shortText: _('Administrátor ústředny'),
					optional: true,
					visibleWhen: data => data.superadmin,
				}];
			},

			getEntityProperties() {
				return [{
					id: 'name',
					name: _('Jméno'),
					sortable: true,
					formatCell(td, v, row) {
						td.textContent = v;

						let admin_level = null;
						if (row.pbxadmin) {
							admin_level = _('Administrátor ústředny');
						} else if (row.superadmin) {
							admin_level = _('Superadmin');
						}

						if (admin_level) {
							td.append(' ');
							td.gwCreateChild({
								nodeName: 'span',
								className: 'superadmin',
								textContent: '(' + admin_level + ')',
							});
						}
					}
				}, {
					id: 'email',
					name: _('E-mail'),
					sortable: true,
					formatCell(td, v, row) {
						td.gwCreateChild({
							nodeName: 'a',
							href: 'mailto:' + v,
							textContent: v
						});
					},
				}, {
					id: 'default_extension',
					name: _('Interní linka'),
					sortable: {
						type: 'numeric',
					},
				}, {
					id: 'external_exten',
					name: _('Externí linka'),
					sortable: {
						type: 'numeric',
					},
				}, {
					id: 'suspended',
					name: _('Aktivní'),
					sortable: {
						type: 'string',
						value: 'celltext',
					},
					formatCell(td, v, row) {
						td.textContent = Formats.bool(!v);
					},
				}];
			},

			'wrap:onCreate'(origFn) {
				let dlg = origFn();

				setTimeout(() => {
					dlg.setFormFieldsData({
						email: '',
						password: '',
					});
				}, 100);
			},

			async setUserSuspended(user, suspended) {
				await REST.PUT('users/' + user.id, {
					suspended,
				});
				await this.reload();
			},

			changePassword(user) {
				let me = this;
				let dlg = new Main.FormDialog({
					title: _('Nastavit heslo uživatele'),

					renderFormFields() {
						return [{
							xtype: 'PasswordTextField',
							name: 'password',
							label: _('Heslo'),
						}];
					},

					async onSave(data) {
						await REST.PUT('users/' + user.id, data);
						await me.reload();
					},
				});
			},
		}, this);
	},

	applyParams(...args) {
		return this.editor.applyParams(...args);
	},
});

// }}}

// Groups:

// {{{ Admin.GroupSelectionOverlay

GW.define('Admin.GroupSelectionOverlay', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-group-selection-overlay ' + (this.cls || ''),
			children: [{
				ref: 'textEl',
				className: 'text',
			}, {
				ref: 'buttonEl',
				nodeName: 'button',
				type: 'button',
				'on:click': ev => {
					this.fire('confirm');
				},
				...this.getButton(),
			}],
		}, this);
	},

	getButton() {
		return {
			className: 'primary icon-left',
			children: [Utils.useIcon('plus'), _('Přidat do skupiny')],
		};
	},

	getSelectionText(n) {
		return _n(n, 'Vybráno *{n} uživatelů*');
	},

	textFormat(s) {
		return s.split(/(\*[^*]*\*)/).filter(v => v).map(v => {
			let m;
			if (m = v.match(/^\*([^*]*)\*$/)) {
				return {
					nodeName: 'strong',
					textContent: m[1],
				}
			} else {
				return v;
			}
		});
	},

	setSelectedCount(n) {
		let text = this.getSelectionText(n);

		this.textEl.gwReplaceChildren(this.textFormat(text));
	},
});

// }}}
// {{{ Admin.SearchableItemSelector

GW.define('Admin.SearchableItemSelector', 'GW.Component', {
	titleText: '',
	searchPlaceholder: _('Hledat'),
	getSelectionText: n => _n(n, 'Vybráno *{n} položek*'),
	emptyText: _('Prázdno'),
	emptyHintText: '',
	cls: '',

	getSearchText(item) {
		return '';
	},

	getColumns() {
		return [];
	},

	getCheckboxColumn() {
		return {
			id: 'cb',
			name: '',
			formatCell(td, v, row) {
				td.gwCreateChild({
					className: 'cb',
					nodeName: 'input',
					type: 'checkbox',
				});
			},
		};
	},

	getSelectionButton() {
		if (this.itemAddText) {
			return {
				className: 'success small icon-left',
				children: [Utils.useIcon('plus'), this.itemAddText],
			};
		} else if (this.itemRemoveText) {
			return {
				className: 'error small icon-left',
				children: [Utils.useIcon('minus'), this.itemRemoveText],
			};
		}
	},

	renderRow(tr, row) {
	},

	initComponent() {
		let me = this;

		this.table = new FilterableSmartTable({
			getColumns: () => [this.getCheckboxColumn(), ...this.getColumns()],
			selectable: true,
			onRowClick: (row, tr, ev) => {
				let cb = tr.querySelector('input.cb');
				if (!ev.target.closest('select,input,a')) {
					cb.checked = !cb.checked;
					me.refreshSelection();
				}
			},
			renderRow: this.renderRow,
			'wrap:updateData'(fn) {
				let selected_items = new Set(me.getSelectedItems().map(i => i.id));

                                fn();

				let cb_td = this.headRow.querySelector('th[data-col="cb"]');

				cb_td.gwReplaceChildren([{
					className: 'cb-all half-checked',
					nodeName: 'input',
					type: 'checkbox',
					'on:input': ev => {
						let cb = ev.target;
						if (!cb.checked) {
							me.clearSelection();
						} else {
							me.selectAllVisible();
						}

					},
				}]);

				me.selectItems(i => selected_items.has(i.id));
				me.refresh();
				me.refreshSelection();
			},
		});

		this.table.el.addEventListener('input', ev => {
			let cb = ev.target.closest('input.cb');
			if (cb) {
				this.refreshSelection();
			}
		}, true);

		this.el = document.gwCreateElement({
			className: 'admin-searchable-item-selector ' + this.cls,
			children: [{
				ref: 'headingEl',
				nodeName: 'h4',
				textContent: this.titleText,
			}, {
				ref: 'searchEl',
				nodeName: 'input',
				type: 'search',
				className: 'search small',
				placeholder: this.searchPlaceholder,
				'on:input': ev => {
					this.refresh();
				},
			}, this.table.el, {
				ref: 'selection',
				xtype: 'Admin.GroupSelectionOverlay',
				cls: 'hidden',
				getButton: () => this.getSelectionButton(),
				getSelectionText: n => this.getSelectionText(n),
				'on:confirm': ev => {
					this.fire('confirmselection');
				},
			}, {
				className: 'empty',
				children: [{
					nodeName: 'img',
					src: APP_BASE + 'img/empty-boxes.svg',
					className: 'illustration',
				}, {
					nodeName: 'h5',
					textContent: this.emptyText,
				}, {
					':skip': !this.emptyHintText,
					nodeName: 'p',
					className: 'text-muted',
					textContent: this.emptyHintText,
				}],
			}, {
				className: 'loading',
				children: [{
					nodeName: 'h3',
					textContent: _('Načítají se data'),
				}, {
					nodeName: 'p',
					textContent: _('Prosíme vyčkejte'),
				}],
			}],
		}, this);
	},

	refreshSelection() {
		let n = this.getSelectedItems().length;
		let cb_all = this.table.headRow.querySelector('input.cb-all');
		let has_checked = !!this.table.body.querySelector('input.cb:checked');
		let has_unchecked = !!this.table.body.querySelector('input.cb:not(:checked)');

		this.selection.el.gwHide(n == 0);
		this.selection.setSelectedCount(n);

		cb_all.classList.toggle('half-checked', has_checked && has_unchecked);
		cb_all.checked = has_checked;
	},

	getSelectedItems() {
		let selection = [];

		for (let cb of this.table.body.querySelectorAll('input.cb')) {
			if (cb.checked) {
				selection.push(cb.closest('tr').$data);
			}
		}

		return selection;
	},

	selectItems(filterFn) {
		for (let cb of this.table.body.querySelectorAll('input.cb')) {
			cb.checked = filterFn(cb.closest('tr').$data);
		}

		this.refreshSelection();
	},

	clearSelection() {
		this.table.body.querySelectorAll('input.cb').forEach(el => el.checked = false);
		this.refreshSelection();
	},

	selectAllVisible() {
		[...this.table.body.querySelectorAll('input.cb')].filter(el => el.offsetWidth).forEach(el => el.checked = true);
		this.refreshSelection();
	},

	selectAll() {
		this.table.body.querySelectorAll('input.cb').forEach(el => el.checked = true);
		this.refreshSelection();
	},

	refresh() {
		let q = this.searchEl.value.trim();
		let match = q ? TextUtils.createMatchAll(q, false) : null;

		for (let tr of this.table.body.querySelectorAll('tr')) {
			let matches = match && !match(this.getSearchText(tr.$data));

			tr.classList.toggle('hidden', !!matches);
		}
	},

	setData(data) {
		this.el.dataset.status = data.length == 0 ? 'empty' : 'loaded';

		this.headingEl.gwReplaceChildren([this.titleText, {
			nodeName: 'span',
			className: 'count',
			textContent: `(${data.length})`,
		}]);

		let selected_items = new Set(this.getSelectedItems().map(i => i.id));
		this.table.setData(data);
		this.selectItems(i => selected_items.has(i.id));

		this.refresh();
		this.refreshSelection();
	},

	setLoading() {
		this.el.dataset.status = 'loading'
	},
});

// }}}

// {{{ Admin.UserGroupsScreen

GW.define('Admin.UserGroupsScreen', 'Admin.Screen', {
	'after:initComponent'() {
		let is_superadmin = Application.isSuperadminUser();

		this.bodyEl.gwReplaceChildren([{
			ref: 'groupList',
			xtype: 'Widgets.EditableItemSelector',
			activeUserGroup: is_superadmin ? null : 'all',
			allowCreate: is_superadmin,
			allowDelete: is_superadmin,
			allowEdit: is_superadmin,
			'on:itemcreate': (c, item) => {
				Application.route({
					paramsFn: () => new URLSearchParams({group: item.id}),
				});
			},
			'on:itemupdate': (c, item) => {
				this.reload();
			},
			'on:itemdelete': (c, item) => {
				this.reload();
			},
		}, {
			ref: 'cards',
			xtype: 'Widgets.TabbedCardPanel',
			cls: 'panel-container',
			getTabs: () => this.getTabs(),
		}], this);
	},

	//PLUGIN: extension point - to adding more tabs
	getTabs() {
		return [{
			id: 'users',
			title: _('Uživatelé'),
			icon: 'user',
			card: {
				xtype: 'Admin.UserGroupsScreen.Members',
			},
		}, {
			id: 'queues',
			title: _('Fronty'),
			icon: 'queue',
			card: {
				xtype: 'Admin.UserGroupsScreen.Queues',
			},
		}, {
			id: 'pauses',
			title: _('Pauzy'),
			icon: 'pause',
			card: {
				xtype: 'Admin.UserGroupsScreen.Pauses',
			},
		}, {
			id: 'apps',
			title: _('Aplikace'),
			icon: 'app',
			card: {
				xtype: 'Admin.UserGroupsScreen.Applications',
			},
		}, {
			id: 'agent-panel-tabs',
			title: _('Záložky AP'),
			icon: 'tabs',
			card: {
				xtype: 'Admin.UserGroupsScreen.AgentPanelTabs',
			},
		}, {
			id: 'contacts',
			title: _('Adresáře'),
			icon: 'book',
			card: {
				xtype: 'Admin.UserGroupsScreen.ContactGroups',
			},
		}, {
			id: 'extensions',
			title: _('Freeseating'),
			icon: 'seat',
			card: {
				xtype: 'Admin.UserGroupsScreen.ExtensionGroup',
			},
		}, {
			id: 'voicemails',
			title: _('Záznamníky'),
			icon: 'voicemail',
			card: {
				xtype: 'Admin.UserGroupsScreen.MailboxGroup',
			},
		}, {
			id: 'speeddial',
			title: _('Rychlé volby'),
			icon: 'dashboard',
			card: {
				xtype: 'Admin.UserGroupsScreen.SpeeddialGroup',
			},
		}, {
			id: 'priorities',
			title: _('Priority'),
			icon: 'priority',
			card: {
				xtype: 'Admin.UserGroupsScreen.Priorities',
			},
		}, {
			id: 'user_conditions',
			title: _('Časové podmínky'),
			icon: 'calendar',
			card: {
				xtype: 'Admin.UserGroupsScreen.UserConditionGroup',
			},
		}, {
			id: 'options',
			title: _('Ostatní nastavení'),
			icon: 'settings',
			card: {
				xtype: 'Admin.UserGroupsScreen.Options',
			},
		}];
	},

	reload() {
		this.applyParams(new URLSearchParams(location.search));
	},

	async applyParams(params) {
		let groupList = this.groupList;
		let {item: group, items: groups} = await this.groupList.applyParams(params);

		if (!group) {
			this.groupList.el.classList.add('hidden');
			this.cards.showEmpty({
				xtype: 'GW.Component',
				initComponent() {
					this.el = document.gwCreateElement({
						className: 'empty',
						children: [{
							nodeName: 'img',
							src: APP_BASE + 'img/empty-boxes.svg',
							className: 'illustration',
						}, {
							nodeName: 'h5',
							textContent: _('Zatím nejsou vytvořeny žádné skupiny'),
						}, {
							':skip': !groupList.allowCreate,
							nodeName: 'button',
							type: 'button',
							className: 'primary icon-left',
							children: [Utils.useIcon('plus'), groupList.addButtonText],
							'on:click': ev => groupList.createItem(),
						}],
					});
				},
			})
			return;
		} else {
			this.groupList.el.classList.remove('hidden');
		}

		let activeTab = params.get('tab');
		if (activeTab) {
			this.last_switched_tab = activeTab
		} else if (this.last_switched_tab) {
			params.set('tab', this.last_switched_tab)
		}

		let {card} = await this.cards.applyParams(params);

		await card.loadData(group);
	},
});

// }}}
// {{{ Admin.UserGroupsScreen.Members

GW.define('Admin.UserGroupsScreen.Members', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-group-association-panel',
			children: [{
				ref: 'included',
				cls: 'included',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns().concat(this.getEditableColumns()),
				getSearchText: item => item.name + ' ' + (item.email || ''),
				titleText: _('Uživatelé skupiny'),
				searchPlaceholder: _('Hledat jméno nebo email'),
				itemRemoveText: _('Odebrat ze skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} uživatelů*'),
				emptyText: _('Ve skupině je zatím prázdno'),
				emptyHintText: _('Uživatele můžete přidat výběrem z pravého sloupce'),
				'on:confirmselection': c => this.onRemoveItems(c.getSelectedItems()),
			}, {
				ref: 'excluded',
				cls: 'excluded',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.name + ' ' + (item.email || ''),
				titleText: _('Ostatní uživatelé'),
				searchPlaceholder: _('Hledat jméno nebo email'),
				itemAddText: _('Přidat do skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} uživatelů*'),
				emptyText: _('Žádní další uživatelé nejsou dostupní'),
				emptyHintText: _('Uživatele můžete založit v konfiguraci uživatelů'),
				'on:confirmselection': c => this.onAddItems(c.getSelectedItems()),
			}]
		}, this);
	},

	getStaticColumns() {
		return [{
			id: 'name',
			name: _('Jméno'),
			sortable: true,
		}, {
			id: 'email',
			name: _('E-mail'),
			sortable: true,
			formatCell(td, v, row) {
				if (!v) {
					return;
				}

				td.gwCreateChild({
					nodeName: 'a',
					href: 'mailto:' + v,
					textContent: v
				});
			},
		}];
	},

	confirmAdminChange() {
		return new Promise((res) => {
			new Main.ConfirmDialog({
				title: _('Ztratíte přístup k administraci skupiny. Pokračovat?'),

				'on:aftersave': (c, data) => {
					res(true);
				},
				'on:destroy': () => {
					res(false);
				},
			});
		});
	},

	getEditableColumns() {
		let me = this;

		return [{
			id: 'role',
			name: _('Práva'),
			formatCell(td, v, row) {
				if (row.superadmin) {
					td.append(_('Superadmin'));
					return;
				}

				td.gwCreateChild({
					xtype: 'SelectField',
					options: me.roles.map(r => {
						return {
							value: r.id,
							text: r.name,
						};
					}),
					value: v,
					origValue: v,
					'on:change': async c => {
						let reload = false;
						if (!Application.isSuperadminUser()) {
							if (c.origValue == 'admin' && c.getValue() != 'admin') {
								reload = true;

								if (!await me.confirmAdminChange()) {
									c.setValue(c.origValue);
									return;
								}
							}
						}

						await me.saveFormField(c, ['user', 'change', {
							id: row.id,
							role: c.getValue(),
						}]);

						if (reload) {
							location.reload();
						}
					},
				});
			},
		}];
	},

	async saveFormField(f, changeset) {
		try {
			f.setDisabled(true);

			await this.saveChangeset([changeset]);

			f.origValue = f.getValue();
		} catch (ex) {
			f.setValue(f.origValue);
			throw ex;
		} finally {
			f.setDisabled(false);
		}
	},

	setDisabled(disabled) {
		this.el.querySelectorAll('input,select').forEach(el => el.disabled = disabled);
	},

	onAddItems(items) {
		return this.saveAssociation(items.map(i => ['user', 'add', {id: i.id}]));
	},

	onRemoveItems(items) {
		return this.saveAssociation(items.map(i => ['user', 'remove', {id: i.id}]));
	},

	async saveChangeset(changeset) {
		await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/associations/changeset', changeset);
	},

	async saveAssociation(changeset) {
		try {
			this.setDisabled(true);

			await this.saveChangeset(changeset);

			this.included.clearSelection();
			await this.reload();
		} finally {
			this.setDisabled(false);
		}
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let assocs = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/associations', null, {signal});
		let users = await REST.GET('users', null, {signal});
		this.roles = await REST.GET('user-roles', null, {signal});

		users = users.filter(u => !u.suspended && !u.superadmin);

		let memberMap = new Map(assocs.members.map(m => [m.id, {
			role: m.role,
		}]));

		this.included.setData(users.map(u => {
			let m = memberMap.get(u.id);

			return m && {
				...u,
				...m,
			};
		}).filter(u => u));

		this.excluded.setData(users.filter(u => !memberMap.has(u.id)));
	},

	async loadData(group) {
		this.group = group;

		this.included.setLoading();
		this.excluded.setLoading();

		await this.reload();
	},
});

// }}}
// {{{ Admin.UserGroupsScreen.Queues

GW.define('Admin.UserGroupsScreen.Queues', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-group-association-panel',
			children: [{
				ref: 'included',
				cls: 'included',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns().concat(this.getEditableColumns()),
				getSearchText: item => item.id + ' ' + (item.name || ''),
				titleText: _('Fronty ve skupině'),
				searchPlaceholder: _('Hledat název nebo číslo'),
				itemRemoveText: _('Odebrat ze skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} front*'),
				emptyText: _('Ve skupině je zatím prázdno'),
				emptyHintText: _('Fronty můžete přidat výběrem z pravého sloupce'),
				'on:confirmselection': c => this.onRemoveItems(c.getSelectedItems()),
				renderRow(tr, row) {
					if (row.removed) {
						tr.classList.add('subdued');
						tr.title = _('Tato fronta již není v systému dostupná');
					}
				},
			}, {
				ref: 'excluded',
				cls: 'excluded',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.id + ' ' + (item.name || ''),
				titleText: _('Dostupné fronty'),
				searchPlaceholder: _('Hledat název nebo číslo'),
				itemAddText: _('Přidat do skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} front*'),
				emptyText: _('Žádné další fronty nejsou dostupné'),
				'on:confirmselection': c => this.onAddItems(c.getSelectedItems()),
			}]
		}, this);
	},

	getStaticColumns() {
		return [{
			id: 'queue',
			name: _('Fronta'),
			sortable: true,
			formatCell(td, v, row) {
				td.textContent = v;

				if (row.kind == 'personal') {
					td.append(' ');
					td.gwCreateChild({
						nodeName: 'span',
						className: 'personal',
						textContent: '(' + _('osobní') + ')',
					});
				}

				if (row.removed) {
					td.classList.add('removed');
					td.title = _('Tato fronta již není dostupná');
				}
			},
		}, {
			id: 'name',
			name: _('Název'),
			sortable: true,
		}];
	},

	getEditableColumns() {
		let me = this;

		return [{
			id: 'owner',
			name: _('Vlastník'),
			formatCell(td, v, row) {
				if (row.kind != 'personal') {
					return;
				}

				td.gwCreateChild({
					xtype: 'SelectField',
					options: me.users.map(u => {
						return {
							value: u.id,
							text: u.name,
							popup_text: u.name + ' - ' + u.email,
						};
					}),
					value: v,
					origValue: v,
					'on:change': async c => {
						await me.saveFormField(c, ['queue', 'change', {
							id: row.id,
							owner: c.getValue(),
						}]);
					},
				});
			},
		}];
	},

	async saveFormField(f, changeset) {
		try {
			f.setDisabled(true);

			await this.saveChangeset([changeset]);

			f.origValue = f.getValue();
		} catch (ex) {
			f.setValue(f.origValue);
			throw ex;
		} finally {
			f.setDisabled(false);
		}
	},

	setDisabled(disabled) {
		this.el.querySelectorAll('input,select').forEach(el => el.disabled = disabled);
	},

	async onAddItems(items) {
		let me = this;
		let owner = null;

		if (items.some(i => i.kind == 'personal')) {
			let dlg = new Main.FormDialog({
				allowCloseButton: false,
				title: _('Vlastník osobní fronty'),
				subtitle: _('Přidáváte do skupiny osobní frontu. Osobní fronty musí mít nastaveny vlastníka. Vyberte jednoho ze stávajícíh členů skupiny.'),

				renderFormFields() {
					return [{
						label: _('Vlastník'),
						name: 'owner',
						xtype: 'SelectField',
						options: me.users.map(u => {
							return {
								value: u.id,
								text: u.name,
								popup_text: u.name + ' - ' + u.email,
							};
						}),
					}];
				},

				async onSave(data) {
					owner = data.owner;
				},
			});

			await dlg.getClosePromise();

			if (!owner) {
				Application.notify({
					kind: 'warning',
					text: _('Je třeba vybrat vlastníka fronty. Fronty nebyly přidány do skupiny.'),
					timeout: 5000,
				});
				return;
			}
		}

		items.filter(i => i.kind == 'personal').forEach(i => i.owner = owner);

		await this.saveAssociation(items.map(i => ['queue', 'add', {id: i.id, owner: i.owner}]));
	},

	onRemoveItems(items) {
		return this.saveAssociation(items.map(i => ['queue', 'remove', {id: i.id}]));
	},

	async saveChangeset(changeset) {
		await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/associations/changeset', changeset);
	},

	async saveAssociation(changeset) {
		try {
			this.setDisabled(true);

			await this.saveChangeset(changeset);

			this.included.clearSelection();
			await this.reload();
		} finally {
			this.setDisabled(false);
		}
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let assocs = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/associations', null, {signal});
		let queues = await REST.GET('queues', null, {signal});

		this.users = await REST.GET('users', null, {signal});

		// filter users
		let member_map = new Map(assocs.members.map(m => [m.id]));
		this.users = this.users.filter(u => !u.suspended && !u.superadmin && member_map.has(u.id));

		queues.forEach(q => q.id = q.queue);

		let queueMap = new Map(queues.map(q => [q.id, q]));
		let includedMap = new Map(assocs.queues.map(q => {
			let queue = queueMap.get(q.id);
			if (queue) {
				return [q.id, {
					...queue,
					owner: q.owner,
				}];
			} else {
				return [q.id, {
					id: q.id,
					queue: q.id,
					removed: true,
				}];
			}
		}));

		this.included.setData(queues.map(q => includedMap.get(q.id)).filter(q => q));
		this.excluded.setData(queues.filter(q => !includedMap.has(q.id) && q.kind != 'system'));
	},

	async loadData(group) {
		this.group = group;

		this.included.setLoading();
		this.excluded.setLoading();

		await this.reload();
	},
});

// }}}
// {{{ Admin.UserGroupsScreen.Pauses

GW.define('Admin.UserGroupsScreen.Pauses', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-group-association-panel',
			children: [{
				ref: 'included',
				cls: 'included',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns().concat(this.getEditableColumns()),
				getSearchText: item => item.name,
				titleText: _('Pauzy ve skupině'),
				searchPlaceholder: _('Hledat název'),
				itemRemoveText: _('Odebrat ze skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} pauz*'),
				emptyText: _('Ve skupině je zatím prázdno'),
				emptyHintText: _('Pauzy můžete přidat výběrem z pravého sloupce'),
				'on:confirmselection': c => this.onRemoveItems(c.getSelectedItems()),
			}, {
				ref: 'excluded',
				cls: 'excluded',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.name,
				titleText: _('Dostupné pauzy'),
				searchPlaceholder: _('Hledat název'),
				itemAddText: _('Přidat do skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} pauz*'),
				emptyText: _('Žádné další pauzy nejsou dostupné'),
				'on:confirmselection': c => this.onAddItems(c.getSelectedItems()),
			}]
		}, this);
	},

	getStaticColumns() {
		return [{
			id: 'name',
			name: _('Název'),
			sortable: true,
		}];
	},

	getEditableColumns() {
		return [];
	},

	setDisabled(disabled) {
		this.el.querySelectorAll('input,select').forEach(el => el.disabled = disabled);
	},

	onAddItems(items) {
		return this.saveAssociation(items.map(i => ['pause_type', 'add', {id: i.id}]));
	},

	onRemoveItems(items) {
		return this.saveAssociation(items.map(i => ['pause_type', 'remove', {id: i.id}]));
	},

	async saveChangeset(changeset) {
		await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/associations/changeset', changeset);
	},

	async saveAssociation(changeset) {
		try {
			this.setDisabled(true);

			await this.saveChangeset(changeset);

			this.included.clearSelection();
			await this.reload();
		} finally {
			this.setDisabled(false);
		}
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let assocs = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/associations', null, {signal});
		let pauses = await REST.GET('pause-types', null, {signal});

		let allMap = new Map(pauses.map(p => [p.id, p]));
		let includedMap = new Map(assocs.pause_types.map(p => [p.id, allMap.get(p.id)]));

		this.included.setData(pauses.map(p => includedMap.get(p.id)).filter(p => p));
		this.excluded.setData(pauses.filter(p => !includedMap.has(p.id)));
	},

	async loadData(group) {
		this.group = group;

		this.included.setLoading();
		this.excluded.setLoading();

		await this.reload();
	},
});

// }}}
// {{{ Admin.UserGroupsScreen.ContactGroups

GW.define('Admin.UserGroupsScreen.ContactGroups', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-group-association-panel',
			children: [{
				ref: 'included',
				cls: 'included',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns().concat(this.getEditableColumns()),
				getSearchText: item => item.name,
				titleText: _('Adresáře ve skupině'),
				searchPlaceholder: _('Hledat název'),
				itemRemoveText: _('Odebrat ze skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} adresářů*'),
				emptyText: _('Ve skupině je zatím prázdno'),
				emptyHintText: _('Adresáře můžete přidat výběrem z pravého sloupce'),
				'on:confirmselection': c => this.onRemoveItems(c.getSelectedItems()),
			}, {
				ref: 'excluded',
				cls: 'excluded',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.name,
				titleText: _('Dostupné adresáře'),
				searchPlaceholder: _('Hledat název'),
				itemAddText: _('Přidat do skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} adresářů*'),
				emptyText: _('Žádné další adresáře nejsou dostupné'),
				'on:confirmselection': c => this.onAddItems(c.getSelectedItems()),
			}]
		}, this);
	},

	getStaticColumns() {
		return [{
			id: 'name',
			name: _('Název'),
			sortable: true,
		}];
	},

	getEditableColumns() {
		let me = this;

		return [{
			id: 'editable_by_role',
			name: _('Editace'),
			formatCell(td, v, row) {
				if (row.type === 'user') {
					td.gwCreateChild({
						xtype: 'SelectField',
						options: [{
							value: 'none',
							text: _('Bez editace')
						}, {
							value: 'agent',
							text: _('Edituje agent')
						}, {
							value: 'supervisor',
							text: _('Edituje supervizor')
						}],
						value: v,
						origValue: v,
						'on:change': async c => {
							await me.saveFormField(c, ['contact_group', 'change', {
								id: row.id,
								editable_by_role: c.getValue(),
							}]);
						},
					});
				}
			},
		}];
	},

	async saveFormField(f, changeset) {
		try {
			f.setDisabled(true);

			await this.saveChangeset([changeset]);

			f.origValue = f.getValue();
		} catch (ex) {
			f.setValue(f.origValue);
			throw ex;
		} finally {
			f.setDisabled(false);
		}
	},

	setDisabled(disabled) {
		this.el.querySelectorAll('input,select').forEach(el => el.disabled = disabled);
	},

	onAddItems(items) {
		return this.saveAssociation(items.map(i => ['contact_group', 'add', {id: i.id}]));
	},

	onRemoveItems(items) {
		return this.saveAssociation(items.map(i => ['contact_group', 'remove', {id: i.id}]));
	},

	async saveChangeset(changeset) {
		await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/associations/changeset', changeset);
	},

	async saveAssociation(changeset) {
		try {
			this.setDisabled(true);

			await this.saveChangeset(changeset);

			this.included.clearSelection();
			await this.reload();
		} finally {
			this.setDisabled(false);
		}
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let assocs = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/associations', null, {signal});
		let groups = await REST.GET('contact-groups', null, {signal});

		let allMap = new Map(groups.map(g => [g.id, g]));
		let includedMap = new Map(assocs.contact_groups.map(g => [
			g.id, 
			{...assocs.contact_groups.find(a => a.id == g.id), ...allMap.get(g.id)}
		]));

		this.included.setData(groups.map(g => includedMap.get(g.id)).filter(g => g));
		this.excluded.setData(groups.filter(g => !includedMap.has(g.id)));
	},

	async loadData(group) {
		this.group = group;

		this.included.setLoading();
		this.excluded.setLoading();

		await this.reload();
	},
});

// }}}
// {{{ Admin.UserGroupsScreen.ExtensionGroup

GW.define('Admin.UserGroupsScreen.ExtensionGroup', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-group-association-panel',
			children: [{
				ref: 'included',
				cls: 'included',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns().concat(this.getEditableColumns()),
				getSearchText: item => item.id,
				titleText: _('Linky ve skupině'),
				searchPlaceholder: _('Hledat linku'),
				itemRemoveText: _('Odebrat ze skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} linek*'),
				emptyText: _('Ve skupině je zatím prázdno'),
				emptyHintText: _('Linky můžete přidat výběrem z pravého sloupce'),
				'on:confirmselection': c => this.onRemoveItems(c.getSelectedItems()),
			}, {
				ref: 'excluded',
				cls: 'excluded',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.id,
				titleText: _('Dostupné linky'),
				searchPlaceholder: _('Hledat linku'),
				itemAddText: _('Přidat do skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} linek*'),
				emptyText: _('Žádné další linky nejsou dostupné'),
				'on:confirmselection': c => this.onAddItems(c.getSelectedItems()),
			}]
		}, this);
	},

	getStaticColumns() {
		return [{
			id: 'id',
			name: _('Linka'),
			sortable: true,
		}];
	},

	getEditableColumns() {
		return [];
	},

	setDisabled(disabled) {
		this.el.querySelectorAll('input,select').forEach(el => el.disabled = disabled);
	},

	onAddItems(items) {
		return this.saveAssociation(items.map(i => ['extension', 'add', {id: i.id}]));
	},

	onRemoveItems(items) {
		return this.saveAssociation(items.map(i => ['extension', 'remove', {id: i.id}]));
	},

	async saveChangeset(changeset) {
		await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/associations/changeset', changeset);
	},

	async saveAssociation(changeset) {
		try {
			this.setDisabled(true);

			await this.saveChangeset(changeset);

			this.included.clearSelection();
			await this.reload();
		} finally {
			this.setDisabled(false);
		}
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let assocs = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/associations', null, {signal});
		let extens = await REST.GET('extensions', null, {signal});

		let included = assocs.extensions.map(g => g.id);
		let excluded = extens.filter(exten => !included.includes(exten.extension))
			.map(exten => {
				return {
					id: exten.extension,
				};
			});

		this.included.setData(assocs.extensions);
		this.excluded.setData(excluded);
	},

	async loadData(group) {
		this.group = group;

		this.included.setLoading();
		this.excluded.setLoading();

		await this.reload();
	},
});

// }}}
// {{{ Admin.UserGroupsScreen.MailboxGroup

GW.define('Admin.UserGroupsScreen.MailboxGroup', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-group-association-panel',
			children: [{
				ref: 'included',
				cls: 'included',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.id + ' ' + (item.name || ''),
				titleText: _('Záznamníky ve skupině'),
				searchPlaceholder: _('Hledat záznamník'),
				itemRemoveText: _('Odebrat ze skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} záznamníků*'),
				emptyText: _('Ve skupině je zatím prázdno'),
				emptyHintText: _('Záznamníky můžete přidat výběrem z pravého sloupce'),
				'on:confirmselection': c => this.onRemoveItems(c.getSelectedItems()),
			}, {
				ref: 'excluded',
				cls: 'excluded',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.id + ' ' + (item.name || ''),
				titleText: _('Dostupné záznamníky'),
				searchPlaceholder: _('Hledat záznamník'),
				itemAddText: _('Přidat do skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} záznamníků*'),
				emptyText: _('Žádné další záznamníky nejsou dostupné'),
				'on:confirmselection': c => this.onAddItems(c.getSelectedItems()),
			}]
		}, this);
	},

	getStaticColumns() {
		return [{
			id: 'mailbox',
			name: _('Záznamník'),
			sortable: true,
		}, {
			id: 'name',
			name: _('Název'),
			sortable: true,
		}];
	},

	setDisabled(disabled) {
		this.el.querySelectorAll('input,select').forEach(el => el.disabled = disabled);
	},

	onAddItems(items) {
		return this.saveAssociation(items.map(i => ['mailbox', 'add', {id: i.id}]));
	},

	onRemoveItems(items) {
		return this.saveAssociation(items.map(i => ['mailbox', 'remove', {id: i.id}]));
	},

	async saveChangeset(changeset) {
		await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/associations/changeset', changeset);
	},

	async saveAssociation(changeset) {
		try {
			this.setDisabled(true);

			await this.saveChangeset(changeset);

			this.included.clearSelection();
			await this.reload();
		} finally {
			this.setDisabled(false);
		}
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let assocs = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/associations', null, {signal});
		let mailboxes = await REST.GET('mailboxes', null, {signal});

		mailboxes.forEach(q => q.id = q.mailbox);
		
		let included_set = new Set(assocs.mailboxes.map(g => g.id));

		let included = mailboxes.filter(mb => included_set.has(mb.mailbox));
		let excluded = mailboxes.filter(mb => !included_set.has(mb.mailbox));

		this.included.setData(included);
		this.excluded.setData(excluded);
	},

	async loadData(group) {
		this.group = group;

		this.included.setLoading();
		this.excluded.setLoading();

		await this.reload();
	},
});

// }}}
// {{{ Admin.UserGroupsScreen.SpeeddialGroup

GW.define('Admin.UserGroupsScreen.SpeeddialGroup', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-group-association-panel',
			children: [{
				ref: 'included',
				cls: 'included',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.id + ' ' + (item.name || ''),
				titleText: _('Záložky ve skupině'),
				searchPlaceholder: _('Hledat záložku'),
				itemRemoveText: _('Odebrat ze skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} záložek*'),
				emptyText: _('Ve skupině je zatím prázdno'),
				emptyHintText: _('Záložky můžete přidat výběrem z pravého sloupce'),
				'on:confirmselection': c => this.onRemoveItems(c.getSelectedItems()),
			}, {
				ref: 'excluded',
				cls: 'excluded',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.id + ' ' + (item.name || ''),
				titleText: _('Dostupné záložky'),
				searchPlaceholder: _('Hledat záložku'),
				itemAddText: _('Přidat do skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} záložek*'),
				emptyText: _('Žádné další záložky nejsou dostupné'),
				'on:confirmselection': c => this.onAddItems(c.getSelectedItems()),
			}]
		}, this);
	},

	getStaticColumns() {
		return [{
			id: 'name',
			name: _('Jméno'),
		}];
	},

	setDisabled(disabled) {
		this.el.querySelectorAll('input,select').forEach(el => el.disabled = disabled);
	},

	onAddItems(items) {
		return this.saveAssociation(items.map(i => ['speeddial_contact_group', 'add', {id: i.id}]));
	},

	onRemoveItems(items) {
		return this.saveAssociation(items.map(i => ['speeddial_contact_group', 'remove', {id: i.id}]));
	},

	async saveChangeset(changeset) {
		await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/associations/changeset', changeset);
	},

	async saveAssociation(changeset) {
		try {
			this.setDisabled(true);

			await this.saveChangeset(changeset);

			this.included.clearSelection();
			await this.reload();
		} finally {
			this.setDisabled(false);
		}
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let assocs = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/associations', null, {signal});
		let contact_groups = await REST.GET('speeddial/groups', {group_only: 1}, {signal});

		let included_set = new Set(assocs.speeddial_contact_groups.map(g => g.id));

		let included = contact_groups.filter(g => included_set.has(g.id));
		let excluded = contact_groups.filter(g => !included_set.has(g.id));

		this.included.setData(included);
		this.excluded.setData(excluded);
	},

	async loadData(group) {
		this.group = group;

		this.included.setLoading();
		this.excluded.setLoading();

		await this.reload();
	},
});

// }}}
// {{{ Admin.UserGroupsScreen.Priorities

GW.define('Admin.UserGroupsScreen.Priorities', 'GW.Component', {
	
	group: null,
	
	initComponent() {
		this.el = document.gwCreateElement({
			children: [{
				className: 'smart-table admin-priority-table',
				children: [{
					nodeName: 'table',
					children: [{
						nodeName: 'tbody',
						children : [{
							nodeName: 'tr',
							children: [{
								nodeName: 'td',
								textContent: _('Penalta skupiny'),
							}, {
								nodeName: 'td',
								textContent: '',
							}, {
								nodeName: 'td',
								children: [{
									ref: 'groupPriorityInputEl',
									nodeName: 'input',
									type: 'text',
									className: 'small',
								}],
							}, {
								nodeName: 'td',
								textContent: '',
							}, {
								nodeName: 'td',
								children: [{
									nodeName: 'button',
									className: 'primary small',
									textContent: _('Uložit'),
									'on:click': ev => {
										const queuePriority = this.groupPriorityInputEl.value;
										if (queuePriority) {
											this.savePriority(null, null, queuePriority);
										}
									},
								}],
							}],
						}],
					}],	
				}],
			}, {
				xtype: 'FilterableSmartTable',
				extraClass: 'admin-priority-table',
				ref: 'tableEl',
				getColumns: () => {
					return [{
						id: 'user_name',
						name: _('Agent'),
						sortable: true,
					}, {
						id: 'queue',
						name: _('Fronta'),
						sortable: true,
						formatCell(td, v, row) {
							td.textContent = row.queue + (row.queue_name ? ' - ' + row.queue_name : '');
						},
					}, {
						id: 'agent_priority',
						name: _('Penalta agenta'),
						sortable: {
							type: 'numeric',
						},
						formatCell: (td, v, row) => {
							const val = v || 0;
							td.gwCreateChild({
								$row: row,
								nodeName: 'input',
								type: 'text',
								className: 'small',
								value: val,
								$key_Escape: ev => {
									ev.target.value = val;
								},
								'on:blur': async ev => {
									const newValue = ev.target.value;
									if (newValue != val) {
										this.savePriority(row.user_id, row.queue, newValue);
										this.groupPriorityInputEl.value = '';
									}
								}
							});
						}
					}, {
						id: 'queue_priority',
						name: _('Penalta fronty'),
						sortable: {
							type: 'numeric',
							value: 'celltext'
						},
						formatCell(td, v, row) {
							td.textContent = row.queue_priority || 0;
						},
					}, {
						id: 'total_priority',
						name: _('Penalta celkem'),
						sortable: {
							type: 'numeric',
							value: 'celltext'
						},
						formatCell(td, v, row) {
							td.textContent = (row.agent_priority || 0) + (row.queue_priority || 0);
						},
					}];
				},
			}]
		}, this);
	},

	async savePriority(user_id, queue, priority) {
		await REST.POST(
			'user-groups/' + encodeURIComponent(this.group.id) + '/queue-priorities', 
			{user_id, queue, priority}
		);

		await this.reload();
	},
	
	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		const data = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/queue-priorities', null, {signal});

		let focusRow = document.activeElement && document.activeElement.$row;

		this.tableEl.setData(data);

		/*
		 * Preserve previous input selection.
		 */
		if (focusRow) {
			let input = Array.from(this.tableEl.el.querySelectorAll('input'))
				.find(el => el.$row.user_id == focusRow.user_id && el.$row.queue == focusRow.queue);
			if (input) {
				input.focus();
				input.select();
			}
		}
	},
	
	async loadData(group) {
		this.group = group;
		await this.reload();
	},
	
	applyParams() {
		// reset group priority input value on activate card
		this.groupPriorityInputEl.value = '';
	},

});

// }}}
// {{{ Admin.UserGroupsScreen.UserConditionGroup

GW.define('Admin.UserGroupsScreen.UserConditionGroup', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-group-association-panel',
			children: [{
				ref: 'included',
				cls: 'included',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.id + ' ' + (item.name || ''),
				titleText: _('Časové podmínky ve skupině'),
				searchPlaceholder: _('Hledat časovou podmínku'),
				itemRemoveText: _('Odebrat ze skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} časových podmínek*'),
				emptyText: _('Ve skupině je zatím prázdno'),
				emptyHintText: _('Časové podmínky můžete přidat výběrem z pravého sloupce'),
				'on:confirmselection': c => this.onRemoveItems(c.getSelectedItems()),
			}, {
				ref: 'excluded',
				cls: 'excluded',
				xtype: 'Admin.SearchableItemSelector',
				getColumns: () => this.getStaticColumns(),
				getSearchText: item => item.id + ' ' + (item.name || ''),
				titleText: _('Dostupné časové podmínky'),
				searchPlaceholder: _('Hledat časové podmínky'),
				itemAddText: _('Přidat do skupiny'),
				getSelectionText: n => _n(n, 'Vybráno *{n} časových podmínek*'),
				emptyText: _('Žádné další časové podmínky nejsou dostupné'),
				'on:confirmselection': c => this.onAddItems(c.getSelectedItems()),
			}]
		}, this);
	},

	getStaticColumns() {
		return [{
			id: 'name',
			name: _('Název časové podmínky'),
			sortable: true,
		}];
	},

	setDisabled(disabled) {
		this.el.querySelectorAll('input,select').forEach(el => el.disabled = disabled);
	},

	onAddItems(items) {
		return this.saveAssociation(items.map(i => ['user_condition', 'add', {id: i.id}]));
	},

	onRemoveItems(items) {
		return this.saveAssociation(items.map(i => ['user_condition', 'remove', {id: i.id}]));
	},

	async saveChangeset(changeset) {
		await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/associations/changeset', changeset);
	},

	async saveAssociation(changeset) {
		try {
			this.setDisabled(true);

			await this.saveChangeset(changeset);

			this.included.clearSelection();
			await this.reload();
		} finally {
			this.setDisabled(false);
		}
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let assocs = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/associations', null, {signal});
		let user_conditions = await REST.GET('user-conditions', null, {signal});

		let included_set = new Set(assocs.user_conditions.map(g => g.id));

		let included = user_conditions.filter(tc => included_set.has(tc.id));
		let excluded = user_conditions.filter(tc => !included_set.has(tc.id));

		this.included.setData(included);
		this.excluded.setData(excluded);
	},

	async loadData(group) {
		this.group = group;

		this.included.setLoading();
		this.excluded.setLoading();
 
		await this.reload();
	},
});

// }}}
// {{{ Admin.UserGroupsScreen.Options

GW.define('Admin.UserGroupsScreen.Options', 'GW.Component', {

	group: null,
	
	initComponent() {
		this.el = document.gwCreateElement({
			ref: 'formEl',
			children: [{
				children: [{
					nodeName: 'h6',
					textContent: _('Automatické otevření karty hovoru') + ':',
				},{
					className: 'subgroup',
					children: [{
						xtype: 'AckCheckboxField',
						name: 'call_card_open_on_ring',
						shortText: _('Automaticky otevřít kartu hovoru při začátku zvonění.'),
						'on:change': el => {
							this.opt_call_card_switch_to_on_open.setDisabled(!el.getValue());
							this.saveOption(el.name, el.getValue());
						},
					}, {
						ref: 'opt_call_card_switch_to_on_open',
						xtype: 'AckCheckboxField',
						name: 'call_card_switch_to_on_open',
						shortText: _('Automaticky přepnout na novou kartu hovoru.'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}, {
						xtype: 'AckCheckboxField',
						name: 'call_card_close_on_end',
						shortText: _('Automaticky uzavřít kartu hovoru při nevyzvednutí hovoru.'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}]
				}]
			}, {
				children: [{
					nodeName: 'h6',
					textContent: _('Zobrazit po ukončení hovoru okno pro') + ':',
				},{
					className: 'subgroup',
					children: [{
						xtype: 'AckCheckboxField',
						name: 'force_select_category',
						shortText: _('Zadání kategorie'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}, {
						xtype: 'AckCheckboxField',
						name: 'force_add_note',
						shortText: _('Zadání poznámky'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}, {
						xtype: 'AckCheckboxField',
						name: 'show_window_for_callback_case_close',
						shortText: _('Uzavření případu zpětného volání (callback)'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}]
				}]
			}, {
				xtype: 'AckCheckboxField',
				name: 'queue_history_limited',
				shortText: _('Omezená historie fronty'),
				'on:change': el => this.saveOption(el.name, el.getValue()),
			}, {
				xtype: 'AckCheckboxField',
				name: 'cdr_only_group_calls',
				shortText: _('V CDR jen skupinové hovory'),
				'on:change': el => this.saveOption(el.name, el.getValue()),
			}, {
				children: [{
					nodeName: 'h6',
					textContent: _('Viditelnost dat') + ':',
				},{
					className: 'subgroup',
					children: [{
						ref: 'dataVisibilityRadio',
						xtype: 'RadioButtonGroup',
						name: 'data_visibility',
						options: [{
							value: 'self',
							text: _('Jen svá data - účastník a agent vidí jen svá data, ostatní role vidí data celé skupiny'),
						}, {
							value: 'group',
							text: _('Data celé skupiny - všechny role vidí data celé skupiny'),
						}],
						'on:change': c => this.saveOption(c.name, c.getValue()),
					}]
				}]
			}, {
				children: [{
					nodeName: 'h6',
					textContent: _('Práva k hovorům') + ':',
				},{
					className: 'subgroup',
					children: [{
						xtype: 'AckCheckboxField',
						name: 'show_recordings',
						shortText: _('Zobrazit nahrávky agentům'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}, {
						xtype: 'AckCheckboxField',
						ref: 'allowRecordingsDownloadCheck',
						name: 'allow_recordings_download',
						shortText: _('Povolit agentům stahování nahrávek'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}, {
						xtype: 'AckCheckboxField',
						ref: 'agentSeesOwnRecordingsOnlyCheck',
						name: 'agent_sees_own_recordings_only',
						shortText: _('Agent vidí jen své nahrávky'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}]
				}]
			}, {
				children: [{
					nodeName: 'h6',
					textContent: _('Považovat agenta jako volného ve frontě') + ':',
				},{
					className: 'subgroup',
					children: [{
						ref: 'freeAgentRadio',
						xtype: 'RadioButtonGroup',
						name: 'free_agent_only_agent_app',
						options: [{
							value: 'only_agent_app',
							text: _('Je li přihlášený v agent panelu'),
						}, {
							value: 'all_devices',
							text: _('Je li přihlášený na libovolném zařízení (v agent panelu či HW telefonu)'),
						}],
						'on:change': el => this.saveOption(el.name, el.getValue() == 'only_agent_app'),
					}, {
						xtype: 'AckCheckboxField',
						name: 'free_agent_include_followme',
						shortText: _('Agent je považovaný jako volný vždy, když má aktivovanou externí linku.'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}]
				}]
			}, {
				children: [{
					nodeName: 'h6',
					textContent: _('Projekty') + ':',
				},{
					className: 'subgroup',
					children: [ {
						xtype: 'AckCheckboxField',
						name: 'projects_show_queues',
						shortText: _('Zobrazit v dialeru fronty jako projekty.'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}, {
						xtype: 'AckCheckboxField',
						name: 'projects_show_personal_queues',
						shortText: _('Zobrazit v dialeru osobní fronty jako projekty.'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}]
				}]
			}, {
				xtype: 'AckCheckboxField',
				name: 'agent_can_manage_others_queues',
				shortText: _('Agent může na záložce Agenti přihlašovat a odhlašovat ostatní agenty z a do front'),
				'on:change': el => this.saveOption(el.name, el.getValue()),
			}, {
				xtype: 'AckCheckboxField',
				name: 'agent_can_only_join_all_queues',
				shortText: _('Agenti se mohou přihlásit/odhlásit jen do všech front'),
				'on:change': el => this.saveOption(el.name, el.getValue()),
			}, {
				xtype: 'AckCheckboxField',
				name: 'recording_play_disable_dial',
				shortText: _('Nahrávky přehrávat vždy na webu'),
				'on:change': el => this.saveOption(el.name, el.getValue()),
			}, {
				xtype: 'AckCheckboxField',
				name: 'keep_queues_on_exten_change',
				shortText: _('Při přepínání linky neodhlašovat z front.'),
				'on:change': el => this.saveOption(el.name, el.getValue()),
			}, {
				xtype: 'AckCheckboxField',
				name: 'show_did',
				shortText: _('Zobrazit Volané číslo (DID) v dialeru'),
				'on:change': el => this.saveOption(el.name, el.getValue()),
			}, {
				xtype: 'AckCheckboxField',
				name: 'show_ivr_missed_calls',
				shortText: _('Zobrazit zmeškané hovory z IVR'),
				'on:change': el => this.saveOption(el.name, el.getValue()),
			}, {
				children: [{
					nodeName: 'h6',
					textContent: _('Notifikace zmeškaných hovorů') + ':',
				},{
					className: 'subgroup',
					children: [ {
						xtype: 'AckCheckboxField',
						name: 'notifications_enabled',
						shortText: _('Notifikace zapnuty'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}, {
						xtype: 'AckCheckboxField',
						name: 'notifications_only_joined',
						shortText: _('Pouze pro přihlášené do fronty.'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}, {
						xtype: 'AckCheckboxField',
						name: 'notifications_only_agents',
						shortText: _('Pouze pro agenty'),
						'on:change': el => this.saveOption(el.name, el.getValue()),
					}]
				}]
			}, {
				xtype: 'AckCheckboxField',
				name: 'dialer_show_contact_note',
				shortText: _('Zobrazit poznámku z adresáře při příchozím hovoru'),
				'on:change': el => this.saveOption(el.name, el.getValue()),
			}, ...this.getAdditionalOptions()],
		}, this);
	},

	//PLUGIN: extension point
	getAdditionalOptions() {
		return [];
	},

	async saveOption(name, value) {
		await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/options', {[name]: value});
		await this.reload();
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		const options = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/options', null, {signal});
		for (let el of this.formEl.querySelectorAll('[data-name]')) {
			el.gwComponent.setValue(options[el.dataset.name]);
		}
		this.freeAgentRadio.setValue(options['free_agent_only_agent_app'] ? 'only_agent_app' : 'all_devices');
		this.opt_call_card_switch_to_on_open.setDisabled(!options['call_card_open_on_ring']);
		this.dataVisibilityRadio.setValue(options['data_visibility'] || 'group');
		this.allowRecordingsDownloadCheck.setDisabled(!options['show_recordings']);
		this.agentSeesOwnRecordingsOnlyCheck.setDisabled(!options['show_recordings']);
	},
	
	async loadData(group) {
		this.group = group;
		await this.reload();
	},

});

// }}}
// {{{ Admin.UserGroupsScreen.AgentPanelTabs

GW.define('Admin.UserGroupsScreen.AgentPanelTabs', 'GW.Component', {

	group: null,
	
	hiddenTabs: null,

	initComponent() {
		this.el = document.gwCreateElement({
			ref: 'formEl',
			children: Office.AgentPanel.prototype.getUnfilteredControlPanelTabs().map(tab => ({
				xtype: 'AckCheckboxField',
				name: tab.id,
				shortText: tab.title,
				'on:change': el => this.saveAgentPanelTab(tab.id, el.getValue()),
			})),
		}, this);
	},

	async saveAgentPanelTab(name, enabled) {
		if (!this.hiddenTabs) {
			return; // not loaded yet
		}
		const newHiddenTabs = enabled ? this.hiddenTabs.filter(t => t != name) : this.hiddenTabs.concat([name]);
		await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/options', {
			hidden_agent_panel_tabs: newHiddenTabs
		});
		await this.reload();
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		const options = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/options', null, {signal});
		const hiddenTabs = options['hidden_agent_panel_tabs'] || [];
		this.hiddenTabs = hiddenTabs;
		this.formEl.querySelectorAll('[data-name]').forEach(el => {
			el.gwComponent.setValue(!hiddenTabs.includes(el.dataset.name));
		});
	},

	async loadData(group) {
		this.group = group;
		await this.reload();
	},

});

// }}}
// {{{ Admin.UserGroupsScreen.Applications

GW.define('Admin.UserGroupsScreen.Applications', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-group-applications-panel',
			children: [{
				ref: 'table',
				nodeName: 'table',
				children: [{
					nodeName: 'thead',
					ref: 'head',
					children: [{
						ref: 'headRow',
						nodeName: 'tr',
					}],
				}, {
					nodeName: 'tbody',
					ref: 'body',
					'on*:change': ev => {
						let el = ev.target.closest('input');
						if (el) {
							this.onCheckboxChange(el);
						}
					},
				}],
			}]
		}, this);
	},

	renderTable(roles, apps, role_access) {
		this.headRow.gwReplaceChildren([{
			nodeName: 'th',
			textContent: _('Aplikace'),
		}, ...roles.map(r => {
			return {
				$data: r,
				nodeName: 'th',
				className: 'role',
				textContent: r.name,
			};
		})]);

		let access_map = new Map(role_access.map(a => [a.app, a.roles]));

		this.body.gwReplaceChildren(apps.map(app => {
			let access = access_map.get(app.id) || [];

			return {
				nodeName: 'tr',
				className: 'app',
				children: [{
					$data: app,
					nodeName: 'th',
					textContent: app.name,
				}, ...roles.map(r => {
					return {
						nodeName: 'td',
						className: 'role',
						children: [{
							'attr:data-role': r.id,
							'attr:data-app': app.id,
							$data: {
								role: r,
								app,
							},
							nodeName: 'input',
							type: 'checkbox',
							checked: access.some(a => a.name == r.id),
						}],
					};
				})],
			};
		}));
	},

	async onCheckboxChange(el) {
		let role = el.dataset.role;
		let app_els = this.body.querySelectorAll('[data-role="' + role + '"]');

		try {
			app_els.forEach(el => el.disabled = true);

			let role_apps = [...this.body.querySelectorAll('input[data-role="' + role + '"]:checked')]
				.map(cb => cb.dataset.app);

			await REST.POST('user-groups/' + encodeURIComponent(this.group.id) + '/role-access/' + encodeURIComponent(role), role_apps);
		} finally {
			app_els.forEach(el => el.disabled = false);
		}
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let roles = await REST.GET('user-roles', null, {signal});
		let apps = await REST.GET('user-roles/applications', null, {signal});
		let access = await REST.GET('user-groups/' + encodeURIComponent(this.group.id) + '/role-access', null, {signal});

		this.renderTable(roles, apps, access);
		this.table.gwHide(false);
	},

	async loadData(group) {
		this.group = group;

		this.renderTable([], [], []);
		this.table.gwHide(true);

		await this.reload();
	},
});

// }}}

// Contacts:

// {{{ Admin.ContactsScreen

GW.define('Admin.ContactsScreen', 'Admin.Screen', {
	'after:initComponent'() {
		let me = this;

		this.bodyEl.gwCreateChild({
			ref: 'groups',
			xtype: 'Widgets.EditableItemSelector',
			emptyListText: _('Zatím nemáte žádný adresář'),
			addButtonText: _('Přidat adresář'),
			createTitle: _('Nový adresář'),
			editTitle: _('Upravit adresář'),
			deleteTitle: _('Opravdu chcete smazat adresář "{name}"?'),
			itemApiPath: 'contact-groups',
			itemQueryParam: 'group',

			'on:itemcreate': (c, item) => {
				Application.route({
					paramsFn: () => new URLSearchParams({group: item.id}),
				});
			},
			'on:itemupdate': (c, item) => {
				this.reload();
			},
			'on:itemdelete': (c, item) => {
				this.reload();
			},
			
			'wrap:getItemMenuOptions'(cb, item) {
				if (!item.editable) {
					return [];
				}
				return cb(item);
			},
			
		}, this);

		this.bodyEl.gwCreateChild({
			ref: 'list',
			xtype: 'Widgets.EntityListEditor',
			title: _('Kontakty'),
			entityApiPrefix: 'contacts',
			createText: _('Přidat kontakt'),
			selectText: _('Vyberte kontakt ze seznamu'),
			editText: _('Upravit kontakt'),
			deleteText: i => _('Opravdu smazat kontakt {name}?').replace('{name}', i.name),
			renderDetail: null,

			'wrap:entityCreate'(cb, data) {
				return cb({
					...data,
					group: this.group.id,
				});
			},

			allowAction(item, action) {
				return this.group && this.group.editable;
			},

			getExtraToolbarItems() {
				return [{
					ref: 'importBtn',
					nodeName: 'button',
					className: 'secondary small icon-right',
					children: [_('Import'), Utils.useIcon('arrow-up')],
					'on:click': ev => this.importContacts(ev),
				}, {
					nodeName: 'button',
					className: 'secondary small icon-right',
					children: [_('Export'), Utils.useIcon('arrow-down')],
					'on:click': ev => this.exportContacts(ev),
				}];
			},

			getCreateFormFields() {
				return [{
					xtype: 'TextField',
					name: 'name',
					label: _('Jméno'),
					optional: true,
				}, {
					xtype: 'TextField',
					name: 'company',
					label: _('Firma'),
					optional: true,
				}, {
					xtype: 'TextField',
					name: 'phone0',
					label: _('Telefon 1'),
					optional: true,
				}, {
					xtype: 'TextField',
					name: 'phone1',
					label: _('Telefon 2'),
					optional: true,
				}, {
					xtype: 'TextField',
					name: 'email',
					type: 'email',
					label: _('E-mail'),
					optional: true,
				}, {
					xtype: 'TextField',
					name: 'note',
					label: _('Poznámka'),
					optional: true,
				}];
			},

			getEditDialogData(item) {
				return {
					...item,
					phone0: (item.phones || [])[0],
					phone1: (item.phones || [])[1],
				};
			},

			getEntityProperties() {
				return [{
					id: 'name',
					name: _('Jméno'),
				}, {
					id: 'company',
					name: _('Firma'),
				}, {
					id: 'phone0',
					name: _('Telefon 1'),
					formatCell: (td, v, row) => td.textContent = (row.phones || [])[0],
				}, {
					id: 'phone1',
					name: _('Telefon 2'),
					formatCell: (td, v, row) => td.textContent = (row.phones || [])[1],
				}, {
					id: 'email',
					name: _('E-mail'),
					formatCell(td, v, row) {
						if (!v) {
							return;
						}

						td.gwCreateChild({
							nodeName: 'a',
							href: 'mailto:' + v,
							textContent: v
						});
					},
				}, {
					id: 'note',
					name: _('Poznámka'),
				}];
			},

			exportXlsx() {
				let params = new URLSearchParams({
					group: this.group.id,
				});

				location.href = API_BASE + 'contacts/export/xlsx?' + params.toString();
			},

			showExportDialog(type) {
				let group = this.group;
				let params = new URLSearchParams({
					group: group.export_key,
				});

				function get_url(params) {
					return location.protocol + '//' + location.host + API_BASE + 'contacts/export/' + type + '?' + params.toString();
				}

				new Main.Dialog({
					title: _('Export kontaktů'),
					subtitle: _('Můžete buď stáhnout soubor s kontakty do vašeho počítače, nebo zkopírovat odkaz do telefonu, pro automatickou synchronizaci kontaktů.'),

					'after:initComponent'() {
						this.bodyEl.gwCreateChild({
							className: 'admin-contacts-export',
							children: [{
								className: 'link',
								nodeName: 'a',
								textContent: get_url(params),
								href: get_url(params),
							}, ...me.getExtraExportURLs(type, group).map(u => {
								return {
									className: 'link',
									nodeName: 'a',
									textContent: u,
									href: u,
								};
							}), {
								nodeName: 'button',
								type: 'button',
								className: 'primary',
								textContent: _('Stáhnout'),
								'on:click': ev => {
									params.set('download', '1');
									location.href = get_url(params);
								},
							}],
						});
					},
				});
			},

			exportContacts(ev) {
				let popup = new PopupMenu({
					options: [{
						text: _('Exportovat XML pro Htek'),
						handle: () => this.showExportDialog('htek'),
					}, {
						text: _('Exportovat XML pro Yealink'),
						handle: () => this.showExportDialog('yealink'),
					}, {
						text: _('Exportovat CSV pro Mitel'),
						handle: () => this.showExportDialog('mitel'),
					}, {
						text: _('Exportovat XLSX'),
						handle: this.exportXlsx.bind(this),
					}],
				});

				PopupManager.show(popup, {
					anchorEl: ev.target.closest('button'),
					forceAxis: 'vertical',
					preferReadingOrder: true,
				});
			},

			showSkippedRows(rows) {
				new Main.Dialog({
					title: _('Během importu nastaly nějaké chyby'),
					subtitle: _('Některé kontakty se nepodařilo naimportovat. Seznam přeskočených kontaktů je níže. Důvodem je chybné tel. číslo či neplatná emailová adresa. Upravte XLSX soubor a import opakujte.'),
					
					'after:initComponent'() {
						this.el.classList.add('fullscreen');
						this.bodyEl.gwCreateChild({
							xtype: 'SmartTable',
							getColumns() {
								return [{
									id: 'name',
									name: _('Jméno'),
								}, {
									id: 'company',
									name: _('Firma'),
								}, {
									id: 'phone0',
									name: _('Telefon 1'),
								}, {
									id: 'phone1',
									name: _('Telefon 2'),
								}, {
									id: 'email',
									name: _('E-mail'),
								}, {
									id: 'note',
									name: _('Poznámka'),
								}];
							},
							rows,
						});
					},
				});
			},

			async importContacts(ev) {
				let file = await Uploads.fileSelect('.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
				let params = new URLSearchParams({
					group: this.group.id,
				});
				let res = await Uploads.uploadFile('contacts/import/xlsx?' + params.toString(), file, {
					headers: {
						'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
					}
				});

				if (res.skipped.length > 0) {
					this.showSkippedRows(res.skipped);
				}

				this.reload();
			},

			updateFilterQuery(query) {
				query.group = this.group.id;
				query.limit = 200;
			},

			async loadList(query) {
				let signal = Utils.abortAndRetry(this, 'reload');
				let queryKey = JSON.stringify(query);

				let [list, listChanged] = await Utils.cache(this, 'list', 60000, this.entityGetAll.bind(this), query, signal);

				if (listChanged || queryKey != this.lastQueryKey) {
					this.lastQueryKey = queryKey;

					this.table.setData(list);

					if (list.length >= 200) {
						this.table.onDisplayRestClick = null;
						this.table.limitMessage = _('Zobrazuje se pouze prvních 200 záznamů. Použijte vyhledávání či export pro práci se všemi záznamy.');
						this.table.renderLimitMessage();
					}
				}
			},
		}, this);
	},

	/*
	 * PLUGIN: extension point for adding more contact export link kinds
	 */
	getExtraExportURLs(kind, group) {
		return [];
	},

	async reload() {
		await this.applyParams(new URLSearchParams(location.search));
	},

	async applyParams(params) {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let {item: group, items: groups} = await this.groups.applyParams(params);

		this.list.setDisabled(!group);

		if (group) {
			params.set('group', group.id);
		} else {
			params.delete('group');
		}

		if (!group) {
			this.list.table.setData([]);
			return;
		}

		this.list.group = group;
		this.list.applyParams(params);
		this.list.importBtn.gwHide(!(group && group.editable));
	},
});

// }}}

// Call category hierarchies:

// {{{ Admin.CallCategoryHierarchies

GW.define('Admin.CallCategoryHierarchies', 'Admin.Screen', {
	'after:initComponent'() {
		this.bodyEl.gwReplaceChildren([{
			ref: 'list1El',
			className: 'categories-list topcategories-list',
			'on*:click': ev => {
				let catEl = ev.target.closest('.category-item');

				let renameEl = ev.target.closest('.rename');
				if (renameEl) {
					this.editCategory(catEl.$cat);
					return;
				}

				let nameEl = ev.target.closest('.name');
				if (nameEl) {
					this.selectCategory(catEl.$cat.id);
				}
			},
		}, {
			ref: 'list2El',
			className: 'categories-list subcategories-list',
			'on*:click': ev => {
				let catEl = ev.target.closest('.category-item');

				let renameEl = ev.target.closest('.rename');
				if (renameEl) {
					this.editCategory(catEl.$cat);
					return;
				}
			},
		}, {
			ref: 'groupsList',
			xtype: 'Widgets.EditableItemSelector',
			emptyListText: _('Zatím nemáte žádnou skupinu kategorií'),
			addButtonText: _('Přidat skupinu kategorií'),
			createTitle: _('Nová skupina kategorií'),
			editTitle: _('Upravit skupinu kategorií'),
			deleteTitle: _('Opravdu chcete smazat skupinu kategorií "{name}"?'),
			itemApiPath: 'call-category-hierarchies',
			itemQueryParam: 'group',

			'on:itemcreate': (c, item) => {
				Application.route({
					paramsFn: () => new URLSearchParams({group: item.id}),
				});
			},
			'on:itemupdate': (c, item) => {
				this.reload();
			},
			'on:itemdelete': (c, item) => {
				this.reload();
			},
		}], this);
	},

	'before:releaseComponent'() {
		this.dndReorder1 && this.dndReorder1.destroy();
		this.dndReorder2 && this.dndReorder2.destroy();
	},

	// Categories:

	renderCategory(cat) {
		return {
			$cat: cat,
			className: 'category-item',
			children: [{
				className: 'name',
				children: [Utils.useIcon('menu'), {
					className: 'text',
					textContent: cat.name,
				}, {
					nodeName: 'button',
					className: 'icon-only rename',
					children: [Utils.useIcon('pencil')],
				}],
			}, {
				nodeName: 'button',
				className: 'secondary icon-only remove',
				children: [Utils.useIcon('minus')],
				'on:click': ev => this.deleteCategory(cat.id),
			}],
		};
	},

	renderCategories(ct, parent) {
		let parentCat = this.categories.find(c => c.id == parent);
		let cats = this.categories.filter(c => {
			return c.parent == parent;
		});

		ct.textContent = '';

		ct.gwCreateChild({
			className: 'title',
			textContent: parentCat ? _c('Podkategorie', 'singular') :  _('Hlavní kategorie'),
		});

		let list = ct.gwCreateChild({
			className: 'list',
			children: cats.map(c => this.renderCategory(c)),
		});

		let dndName = parent ? 'dndReorder2' : 'dndReorder1';

		this[dndName] && this[dndName].destroy();
		this[dndName] = DnDListSetup(list, {
			startFilterClass: 'category-item',
			itemClass: 'category-item',
			scrollCt: ct,
			onDragEnd: () => this.saveCategoriesOrder(ct),
		});

		let onAdd = async el => {
			let value = el.children[0].value.trim();
			if (value) {
				try {
					el.querySelectorAll('input,button').forEach(el => el.disabled = true);

					await this.addCategory(parent, value);

					el.children[0].value = '';
				} finally {
					el.querySelectorAll('input,button').forEach(el => el.disabled = false);
				}
			}
		};

		ct.gwCreateChild({
			className: 'new-item-editor',
			children: [{
				nodeName: 'input',
				placeholder: _('Název nové kategorie'),
				'on:keydown': ev => {
					if (ev.key == 'Enter') {
						onAdd(ev.target.parentNode);
					}
				},
			}, {
				nodeName: 'button',
				className: 'secondary icon-only',
				children: [Utils.useIcon('plus')],
				'on:click': ev => {
					onAdd(ev.target.closest('button').parentNode);
				},
			}],
		});
	},

	selectCategory(id) {
		Utils.selectListItem(this.list1El, '.category-item', 'selected', i => i.$cat.id === id);

		if (this.list1El.querySelector('.category-item.selected')) {
			this.renderCategories(this.list2El, id);
			this.list2El.querySelector('.new-item-editor input').focus();
		} else if (this.categories.length > 0) {
			this.list2El.gwReplaceChildren([{
				className: 'empty',
				textContent: _('Vyberte kategorii vlevo'),
			}]);
		} else {
			this.list2El.textContent = '';
		}
	},

	onCategoriesChanged(parent, ct) {
		let newCats = Array.from(ct.querySelectorAll('.category-item')).map(el => el.$cat);

		this.categories = this.categories.filter(c => c.parent != parent).concat(newCats);
	},

	async deleteCategory(id) {
		await REST.DELETE('call-category-hierarchies/' + encodeURIComponent(this.group.id) + '/categories/' + encodeURIComponent(id));

		let cat = this.categories.find(c => c.id == id);

		this.categories = this.categories.filter(c => c.id != id);
		this.refreshCategories();

		if (cat && cat.parent) {
			this.list2El.querySelector('.new-item-editor input').focus();
		} else {
			this.list1El.querySelector('.new-item-editor input').focus();
		}

		await this.saveCategoriesOrder();
	},

	async addCategory(parent, name) {
		let cat = await REST.POST('call-category-hierarchies/' + encodeURIComponent(this.group.id) + '/categories', {
			parent,
			name,
		});

		this.categories.push(cat);
		this.refreshCategories();

		if (parent) {
			this.list2El.querySelector('.new-item-editor input').focus();
		} else {
			this.selectCategory(cat.id);
			this.list1El.querySelector('.new-item-editor input').focus();
		}

		await this.saveCategoriesOrder();
	},

	async editCategory(cat) {
		let me = this;
		let dlg = new Main.FormDialog({
			title: _('Přejmenovat kategorii'),

			renderFormFields() {
				return [{
					xtype: 'TextField',
					name: 'name',
					label: _('Jméno'),
				}];
			},

			async onSave(data) {
				await REST.PUT('call-category-hierarchies/' + encodeURIComponent(me.group.id) + '/categories/' + encodeURIComponent(cat.id), data);

				cat.name = data.name;
				me.refreshCategories();
			},
		});

		dlg.setFormFieldsData(cat);
	},

	async saveCategoriesOrder(ct) {
		if (ct) {
			/*
			 * Apply order from container to this.categories
			 */
			let cats = Array.from(ct.querySelectorAll('.category-item')).map(el => el.$cat);
			let catsSet = new Set(cats);

			this.categories = this.categories.filter(c => !catsSet.has(c)).concat(cats);
		}

		/*
		 * Save this.categories order to database
		 */
		await REST.POST('call-category-hierarchies/' + encodeURIComponent(this.group.id) + '/categories/order',
			this.categories.map(c => c.id));
	},

	refreshCategories() {
		let sel = this.list1El.querySelector('.category-item.selected');

		this.renderCategories(this.list1El, null);
		this.selectCategory(sel ? sel.$cat.id : null);
	},

	async loadCategories(id) {
		let signal = Utils.abortAndRetry(this, 'reload');

		this.group = await REST.GET('call-category-hierarchies/' + encodeURIComponent(id), null, {signal});

		this.categories = this.group.categories || [];

		this.selectCategory();
		this.refreshCategories();
	},

	reload() {
		Utils.clearCache(this);
		this.applyParams(new URLSearchParams(location.search));
	},

	async applyParams(params) {
		let {item: group, items: groups} = await this.groupsList.applyParams(params);

		this.group = group;

		if (!group) {
			this.list1El.gwReplaceChildren([{
				className: 'empty',
				textContent: _('Nejprve založte skupinu kategorií'),
			}]);
			this.list2El.textContent = '';
			return;
		}

		this.loadCategories(group.id);
	},
});

// }}}

// Queues:

// {{{ Admin.EditQueueDialog

GW.define('Admin.EditQueueDialog', 'Main.FormDialog', {
	title: _('Upravit frontu'),

	'after:initComponent'() {
		
		const data = {...this.queue};
		if (data.wrapup_time == 0) {
			data.wrapup_time_infinite = true;
		}
		
		this.setFormFieldsData(data);
		this.traverseChildren(f => {
			if (f.name && f.setValue) {
				// update values in disabled inputs in queue intervals tab
				f.fire('change');
			}
		});
		this.selectTab('common');
	},
	
	selectTab(id) {
		this.tabs.setSelected(id);
		for (let card of this.cards.children) {
			card.gwHide(card.dataset.id != id);
		}
	},
	
	updateIntervalInputs(inputs, eventSource) {
		const [input1Min, input1Max, input2Min, input2Max, input3Min, input3Max] = inputs;
		var val = eventSource.getValue();
		if (eventSource === input1Max) {
			if (val === null) {
				input1Min.setValue(null);
				if (input2Max.getValue() === null) {
					input2Min.setValue(null);
				} else {
					input2Min.setValue('0');
				}
			} else {
				input1Min.setValue('0');
				if (input2Max.getValue() === null) {
					input3Min.setValue(val + 1);
				} else {
					input2Min.setValue(val + 1);
					if (input2Max.getValue() <= val + 1) {
						input2Max.setValue(val + 1);
						this.updateIntervalInputs(inputs, input2Max);
					}
				}
			}
		}
		if (eventSource === input2Max) {
			if (val === null) {
				input3Min.setValue(input1Max.getValue() !== null ? (input1Max.getValue() + 1) : '0');
				input2Min.setValue(null);
			} else {
				input3Min.setValue(val + 1);
				if (input2Min.getValue() !== null && input2Min.getValue() > val) {
					input1Max.setValue(val - 1);
					this.updateIntervalInputs(inputs, input1Max);
				} else if (input1Max.getValue() !== null) {
					input2Min.setValue(input1Max.getValue() + 1);
				}
			}
		}
	},
	
	renderIntervalsFormFields() {
		return [{
			nodeName: 'h5',
			textContent: _('Intervaly pro správné zobrazení stavů v Agent panelu'),
		}, {
			className: 'intervals-container',
			children: [{
				nodeName: 'h6',
				textContent: _('Počet volných agentů'),
			}, {
				className: 'interval-grid',
				children: [{
					className: 'button small static error',
					textContent: _('Není v pořádku!'),
				}, {
					nodeName: 'label',
					textContent: _('Od'),
				}, {
					ref: 'inputFreeAgentsBadMin',
					readOnly: true,
					disabled: true,
					xtype: 'TextField',
					value: '0',
					extraClass: 'small',
				}, {
					nodeName: 'label',
					textContent: _('Do'),
				}, {
					ref: 'inputFreeAgentsBadMax',
					xtype: 'SpinnerField',
					name: 'free_agents_bad_max',
					min: 0,
					optional: true,
					extraClass: 'small',
					'on:change': c => {
						this.updateIntervalInputs([
							this.inputFreeAgentsBadMin, this.inputFreeAgentsBadMax,
							this.inputFreeAgentsWarnMin, this.inputFreeAgentsWarnMax,
							this.inputFreeAgentsOkMin, this.inputFreeAgentsOkMax,
						], c);
					},
				}]
			}, {
				className: 'interval-grid',
				children: [{
					className: 'button small static warning-secondary',
					textContent: _('Pozor'),
				}, {
					nodeName: 'label',
					textContent: _('Od'),
				}, {
					ref: 'inputFreeAgentsWarnMin',
					readOnly: true,
					disabled: true,
					xtype: 'TextField',
					value: '0',
					extraClass: 'small',
				}, {
					nodeName: 'label',
					textContent: _('Do'),
				}, {
					ref: 'inputFreeAgentsWarnMax',
					xtype: 'SpinnerField',
					name: 'free_agents_warn_max',
					optional: true,
					min: 0,
					extraClass: 'small',
					'on:change': c => {
						this.updateIntervalInputs([
							this.inputFreeAgentsBadMin, this.inputFreeAgentsBadMax,
							this.inputFreeAgentsWarnMin, this.inputFreeAgentsWarnMax,
							this.inputFreeAgentsOkMin, this.inputFreeAgentsOkMax,
						], c);
					},
				}]
			}, {
				className: 'interval-grid',
				children: [{
					className: 'button small static success-secondary',
					textContent: _('V pořádku'),
				}, {
					nodeName: 'label',
					textContent: _('Od'),
				}, {
					ref: 'inputFreeAgentsOkMin',
					readOnly: true,
					disabled: true,
					xtype: 'TextField',
					value: '0',
					extraClass: 'small',
				}, {
					nodeName: 'label',
					textContent: _('Do'),
				}, {
					ref: 'inputFreeAgentsOkMax',
					readOnly: true,
					disabled: true,
					xtype: 'TextField',
					value: '-',
					extraClass: 'small',
				}]
			}, {
				nodeName: 'h6',
				textContent: _('Počet čekajících hovorů'),
			}, {
				className: 'interval-grid',
				children: [{
					className: 'button small static success-secondary',
					textContent: _('V pořádku'),
				}, {
					nodeName: 'label',
					textContent: _('Od'),
				}, {
					ref: 'inputWaitingCallsOkMin',
					readOnly: true,
					disabled: true,
					xtype: 'TextField',
					value: '0',
					extraClass: 'small',
				}, {
					nodeName: 'label',
					textContent: _('Do'),
				}, {
					ref: 'inputWaitingCallsOkMax',
					xtype: 'SpinnerField',
					name: 'waiting_calls_ok_max',
					optional: true,
					min: 0,
					extraClass: 'small',
					'on:change': c => {
						this.updateIntervalInputs([
							this.inputWaitingCallsOkMin, this.inputWaitingCallsOkMax,
							this.inputWaitingCallsWarnMin, this.inputWaitingCallsWarnMax,
							this.inputWaitingCallsBadMin, this.inputWaitingCallsBadMax,
						], c);
					}, 
				}]
			}, {
				className: 'interval-grid',
				children: [{
					className: 'button small static warning-secondary',
					textContent: _('Pozor'),
				}, {
					nodeName: 'label',
					textContent: _('Od'),
				}, {
					ref: 'inputWaitingCallsWarnMin',
					readOnly: true,
					disabled: true,
					xtype: 'TextField',
					value: '0',
					extraClass: 'small',
				}, {
					nodeName: 'label',
					textContent: _('Do'),
				}, {
					ref: 'inputWaitingCallsWarnMax',
					xtype: 'SpinnerField',
					name: 'waiting_calls_warn_max',
					optional: true,
					min: 0,
					extraClass: 'small',
					'on:change': c => {
						this.updateIntervalInputs([
							this.inputWaitingCallsOkMin, this.inputWaitingCallsOkMax,
							this.inputWaitingCallsWarnMin, this.inputWaitingCallsWarnMax,
							this.inputWaitingCallsBadMin, this.inputWaitingCallsBadMax,
						], c);
					},
				}]
			}, {
				className: 'interval-grid',
				children: [{
					className: 'button small static error',
					textContent: _('Není v pořádku!'),
				}, {
					nodeName: 'label',
					textContent: _('Od'),
				}, {
					ref: 'inputWaitingCallsBadMin',
					readOnly: true,
					disabled: true,
					xtype: 'TextField',
					value: '0',
					extraClass: 'small',
				}, {
					nodeName: 'label',
					textContent: _('Do'),
				}, {
					ref: 'inputWaitingCallsBadMax',
					readOnly: true,
					disabled: true,
					xtype: 'TextField',
					value: '-',
					extraClass: 'small',
				}]
			}]
		}]
	},

	renderFormFields() {
		return [{
			className: 'tab-switcher',
			xtype: 'Widgets.TabSwitcher',
			ref: 'tabs',
			items: [{
				id: 'common',
				title: _('Obecné'),
				handle: i => this.selectTab(i.id),
			}, {
				id: 'intervals',
				title: _('Intervaly'),
				handle: i => this.selectTab(i.id),
			}, {
				id: 'other',
				title: _('Ostatní'),
				handle: i => this.selectTab(i.id),
			}],
		}, {
			ref: 'cards',
			children: [{
				'attr:data-id': 'common',
				children: [{
					name: 'queue',
					label: _('Fronta'),
					readOnly: true,
					xtype: 'TextField',
				}, {
					name: 'kind',
					label: _('Typ'),
					xtype: 'SelectField',
					options: [{
						value: 'agent',
						text: _('Agentská'),
					}, {
						value: 'personal',
						text: _('Osobní'),
					}, {
						value: 'system',
						text: _('Systémová'),
					}],
				}, {
					name: 'name',
					label: _('Název'),
					xtype: 'TextField',
					optional: true,
				}, {
					name: 'description',
					label: _('Popis'),
					xtype: 'TextField',
					optional: true,
				}, {
					':skip': env.IS_BCCP23,
					name: 'use_smart_agent_context',
					shortText: _('Přihlašovat jako smart agent'),
					xtype: 'AckCheckboxField',
					optional: true,
				}, {
					name: 'prevent_last_agent_leave',
					shortText: _('Nedovolit poslednímu přihlášenému agentovi odhlášení'),
					xtype: 'AckCheckboxField',
					optional: true,
				}]
			}, {
				'attr:data-id': 'intervals',
				children: this.renderIntervalsFormFields(),
			}, {
				'attr:data-id': 'other',
				children: [{
					//XXX: deprecated
					':skip': env.IS_BCCP23,
					xtype: 'AckCheckboxField',
					name: 'wrapup_time_infinite',
					shortText: _('Zapnout neomezenou autopauzu'),
					optional: true,
					'on:change': el => {
						if (el.getValue()) {
							this.wrapupTime.setValue('');
						}
						this.wrapupTime.setDisabled(el.getValue());
					}
				}, {
					//XXX: deprecated
					':skip': env.IS_BCCP23,
					name: 'wrapup_time',
					label: _('Auto pauza [s]'),
					optional: true,
					xtype: 'TextField',
					type: 'number',
					ref: 'wrapupTime'
				}, {
					name: 'priority',
					label: _('Penalta'),
					xtype: 'TextField',
					type: 'number',
				}, {
					xtype: 'SelectField',
					name: 'category_hierarchy',
					label: _('Kategorie'),
					placeholder: _('Žádná'),
					optional: true,
					transformOption(o) {
						return {
							value: o.id,
							text: o.name,
						};
					},
					async getOptions({signal}) {
						let cats = await REST.GET('call-category-hierarchies', null, {signal});

						return [{
							value: null,
							text: _('Žádná'),
						}, ...cats.map(e => {
							return {
								value: e.id,
								text: e.name,
							};
						})];
					},
				}, {
					xtype: 'AckCheckboxField',
					name: 'supports_out_prefix',
					shortText: _('Odchozí prefix'),
					longText: _('Nastavena odchozí routa s prefixem'),
					optional: true,
				}],
			}],
		}];
	},

	async onSave(formData) {
		const {wrapup_time_infinite, ...data} = formData;
		data.wrapup_time = wrapup_time_infinite ? 0 : (data.wrapup_time || null); // TODO check 0 -> will disable wrapup time (without checked infinite)
		
		await REST.PUT('queues/' + encodeURIComponent(this.queue.queue), data);
	},
});

// }}}
// {{{ Admin.QueuesScreen

GW.define('Admin.QueuesScreen', 'Admin.Screen', {
	'after:initComponent'() {
		let queueType = {
			agent: _('Agentská'),
			system: _('Systémová'),
			personal: _('Osobní'),
		};

		function getQueueStatus(queue) {
			if (!queue.kind) {
				return ['unset', _('Nenastavená'), 'cross', 'error-secondary'];
			} else if (queue.kind == 'system') {
				return ['system', _('Systémová'), 'eyeoff', 'warning-secondary'];
			}

			return ['configured', _('Nastavená'), 'check', 'success-secondary'];
		}

		this.bodyEl.gwCreateChild({
			ref: 'editor',
			xtype: 'Widgets.EntityListEditor',
			title: _('Nastavení front'),
			idProp: 'queue',
			entityApiPrefix: 'queues',
			createText: _('Nová fronta'),
			selectText: _('Vyberte frontu ze seznamu'),
			editText: _('Upravit frontu'),
			deleteText: i => _('Opravdu smazat frontu {name}?').replace('{name}', i.name),
			renderDetail: null,

			allowAction(item, action) {
				return action == 'edit';
			},

			filterListItem(i, match) {
				return match([
					i.queue,
					i.name || '',
					i.description || '',
					queueType[i.kind] || i.kind,
					getQueueStatus(i)[1],
					(i.category_hierarchy && i.category_hierarchy.name) || '',
				].join(' '));
			},

			onEdit(item) {
				new Admin.EditQueueDialog({
					queue: item,
					'on:aftersave': c => {
						this.reload();
					},
				});
			},

			getEntityProperties() {
				return [{
					id: 'queue',
					name: _('Fronta'),
					sortable: {
						type: 'numeric',
					},
				}, {
					id: 'kind',
					name: _('Typ'),
					sortable: true,
					formatCell(td, v, row) {
						td.textContent = queueType[v] || v;
					},
				}, {
					id: 'name',
					name: _('Název'),
					sortable: true,
				}, {
					id: 'description',
					name: _('Popis'),
					sortable: true,
				}, {
					id: 'status',
					name: _('Stav'),
					sortable: {
						type: 'string',
						value: 'celltext',
					},
					formatCell(td, v, row) {
						let [status, text, icon, className] = getQueueStatus(row);

						td.gwCreateChild({
							nodeName: 'span',
							className: 'button small icon-left static ' + className,
							'attr:data-status': status,
							children: [Utils.useIcon(icon), text],
						});
					},
				}, {
					//XXX: deprecated
					hidden: env.IS_BCCP23,
					id: 'wrapup_time',
					name: _('Auto pauza [s]'),
					sortable: {
						type: 'numeric',
					},
					formatCell(td, v, row) {
						td.textContent = v === 0 ? _('Neomezená') : v;
					}
				}, {
					id: 'priority',
					name: _('Penalta'),
					sortable: {
						type: 'numeric',
					},
				}, {
					id: 'category_hierarchy',
					name: _('Kategorie'),
					sortable: {
						type: 'string',
						value: 'celltext',
					},
					formatCell(td, v, row) {
						td.textContent = v ? v.name : '';
					},
				}, {
					id: 'supports_out_prefix',
					name: _('Odchozí prefix'),
					sortable: {
						type: 'string',
						value: 'celltext',
					},
					formatCell(td, v, row) {
						td.textContent = v ? _('Ano') : _('Ne');
					},
				}].filter(c => !c.hidden);
			},
		}, this);
	},

	applyParams(...args) {
		return this.editor.applyParams(...args);
	},
});

// }}}
// {{{ Admin.MailboxesScreen

GW.define('Admin.MailboxesScreen', 'Admin.Screen', {
	'after:initComponent'() {

		this.bodyEl.gwCreateChild({
			ref: 'editor',
			xtype: 'Widgets.EntityListEditor',
			title: _('Nastavení záznamníků'),
			idProp: 'mailbox',
			entityApiPrefix: 'mailboxes',
			selectText: _('Vyberte záznamník ze seznamu'),
			renderDetail: null,

			allowAction(item, action) {
				return action == 'edit';
			},

			filterListItem(i, match) {
				return match([
					i.mailbox,
					i.name
				].join(' '));
			},

			getCreateFormFields() {
				return [{
					xtype: 'TextField',
					name: 'name',
					label: _('Název'),
				}, {
					xtype: 'AckCheckboxField',
					name: 'email_enabled',
					optional: true,
					shortText: _('Odesílat nové záznamy na e-mail.'),
				}, {
					xtype: 'TextField',
					name: 'email_recipients',
					optional: true,
					label: _('E-mailové adresy pro odeslání nových záznamů'),
				}];
			},

			getEntityProperties() {
				return [{
					id: 'mailbox',
					name: _('Záznamník'),
					sortable: {
						type: 'numeric',
					},
				}, {
					id: 'name',
					name: _('Název'),
					sortable: true,
				}];
			},
		}, this);
	},

	applyParams(...args) {
		return this.editor.applyParams(...args);
	},
});

// }}}
// {{{ Admin.SpeeddialsScreen

GW.define('Admin.SpeeddialsScreen', 'Admin.Screen', {
	'after:initComponent'() {
		this.bodyEl.gwCreateChild({
			ref: 'groups',
			xtype: 'Widgets.EditableItemSelector',
			emptyListText: _('Zatím nemáte žádné rychlé volby'),
			addButtonText: _('Přidat záložku'),
			createTitle: _('Nová záložka'),
			editTitle: _('Upravit záložku'),
			itemApiPath: 'speeddial/groups',
			itemQueryParam: 'group',

			'on:itemcreate': (c, item) => {
				Application.route({
					paramsFn: () => new URLSearchParams({group: item.id}),
				});
			},
			'on:itemupdate': (c, item) => {
				this.applyParams(new URLSearchParams(location.search));
			},
			'on:itemdelete': (c, item) => {
				this.applyParams(new URLSearchParams(location.search));
			},

			getGetAllParams: () => ({group_only: 1}),
		}, this);

		this.bodyEl.gwCreateChild({
			className: 'entity-list-editor no-detail',
			children: [{
				className: 'header',
				children: [{
					className: 'title',
					textContent:  _('Kontakty'),
				}, {
					ref: 'toolbarCt',
					className: 'toolbar',
					children: [{
						ref: 'createContactCallButton',
						nodeName: 'button',
						type: 'button',
						className: 'primary small icon-left',
						children: [Utils.useIcon('plus'), _('Přidat kontakt')],
						'on:click': ev => this.speeddialEl.openContactCallDialog(),
					}, {
						ref: 'createContactMessageButton',
						nodeName: 'button',
						type: 'button',
						className: 'primary small icon-left',
						children: [Utils.useIcon('plus'), _('Přidat Zprávu')],
						'on:click': ev => this.speeddialEl.openContactMessageDialog(),
					}],
				}],
			}, {
				ref: 'speeddialEl',
				xtype: 'Office.Speeddial',
				renderToolbar: () => [],
				renderCardControls(contact) {
					return [this.renderCardControlsOptions(contact)];
				},
				loadData()  {
					// nothing to do, groupsInFormSelect are set in applyParam()
				},
				openCreateContactGroupDialog: () => {
					// group management is not in this component, 
					// delegate group creating to EditableItemSelector 
					// if there are no groups, button to create a new groups is shown
					// and this is called in on:click handler
					this.groups.createItem();
				},
			}],

		}, this);
	},

	async applyParams(params) {
		let {item: group, items: groups} = await this.groups.applyParams(params);

		this.createContactCallButton.disabled = !group;
		this.createContactMessageButton.disabled = !group;

		if (group) {
			params.set('group', group.id);
		} else {
			params.delete('group');
		}

		this.speeddialEl.groupsInFormSelect = groups;
		await this.speeddialEl.setSelectedGroup(group);
	},
});

// }}}

// Extensions:

// {{{ Admin.EditExtensionDialog

Admin.HWPHONE_CONTROL_OPTIONS = [{
	value: 'originate',
	text: _('Originace z ústředny'),
}, {
	value: 'vendor-api',
	text: _('API výrobce telefonu'),
}, {
	value: 'vendor-api-noyealink',
	text: _('API výrobce telefonu (vyjma Yealink)'),
}];

GW.define('Admin.EditExtensionDialog', 'Main.FormDialog', {
	title: _('Upravit nastavení linky'),

	'before:initComponent'() {
		this.title += ' ' + this.extension.extension;
	},

	'after:initComponent'() {
		let data = {
			...this.extension,
			hwphone_autoanswer: this.extension.hwphone_autoanswer ? 'yes' : 'no',
		};

		this.setFormFieldsData(data);
	},

	renderFormFields() {
		return [{
			name: 'name',
			label: _('Název'),
			xtype: 'TextField',
			optional: true,
		}, {
			name: 'hwphone_control',
			label: _('Ovládání HW telefonu'),
			xtype: 'SelectField',
			options: Admin.HWPHONE_CONTROL_OPTIONS,
		}, {
			name: 'hwphone_autoanswer',
			label: _('Automatické vyzvednutí'),
			xtype: 'SelectField',
			options: [{
				value: 'no',
				text: _('Ne'),
			}, {
				value: 'yes',
				text: _('Ano'),
			}],
		}];
	},

	async onSave(data) {
                data.hwphone_autoanswer = data.hwphone_autoanswer == 'yes';

		await REST.PUT('extensions/' + encodeURIComponent(this.extension.extension), data);
	},
});

// }}}
// {{{ Admin.ExtensionsScreen

GW.define('Admin.ExtensionsScreen', 'Admin.Screen', {
	'after:initComponent'() {
		let controlTypes = new Map(Admin.HWPHONE_CONTROL_OPTIONS.map(o => [o.value, o.text]));

		this.bodyEl.gwCreateChild({
			ref: 'editor',
			xtype: 'Widgets.EntityListEditor',
			title: _('Nastavení linek'),
			idProp: 'extension',
			entityApiPrefix: 'extensions',
			createText: _('Nová fronta'),
			selectText: _('Vyberte frontu ze seznamu'),
			editText: _('Upravit frontu'),
			deleteText: i => _('Opravdu smazat frontu {name}?').replace('{name}', i.name),
			renderDetail: null,

			getExtraToolbarItems() {
				return [{
					nodeName: 'button',
					className: 'secondary small icon-right',
					children: [_('Export'), Utils.useIcon('arrow-down')],
					'on:click': ev => this.exportExtens(ev),
				}];
			},

			allowAction(item, action) {
				return action == 'edit';
			},

			filterListItem(i, match) {
				return match([
					i.extension,
					i.name || '', 
					...i.devices.map(d => d.user_agent || ''),
					...i.devices.filter(d => d.reachable && d.user_agent && d.kind != 'webrtc' && d.ip_address).map(a => a.ip_address.replace(/:.*$/, '')),
				].join(' '));
			},

			onEdit(item) {
				new Admin.EditExtensionDialog({
					extension: item,
					'on:aftersave': c => {
						this.reload();
					},
				});
			},

			getEntityProperties() {
				return [{
					id: 'extension',
					name: _('Linka'),
					sortable: {
						type: 'numeric',
					},
				}, {
					id: 'name',
					name: _('Název'),
					sortable: true,
				}, {
					id: 'hwphone_control',
					name: _('Ovládání HW telefonu'),
					sortable: {
						type: 'string',
						value: 'celltext',
					},
					formatCell(td, v, row) {
						td.textContent = controlTypes.get(v) || v;
					},
				}, {
					id: 'hwphone_autoanswer',
					name: _('Automatické vyzvednutí'),
					format: 'bool',
					sortable: true,
				}, {
					id: 'devices',
					name: _('Připojená zařízení'),
					sortable: {
						type: 'string',
						value: 'celltext',
					},
					formatCell(td, v, row) {
						td.textContent = v.filter(d => d.reachable && d.kind != 'webrtc').map(d => d.user_agent || _('Neznámé zařízení')).join(', ');
					},
				}, {
					id: 'ip_addresses',
					name: _('IP adresy'),
					sortable: {
						type: 'string',
						value: 'celltext',
					},
					formatCell(td, v, row) {
						for (let d of row.devices.filter(d => d.reachable && d.kind != 'webrtc' && d.ip_address)) {
							if (td.firstChild) {
								td.append(', ');
							}

							let ip_address = d.ip_address.replace(/:.*$/, '');

							td.gwCreateChild({
								nodeName: 'a',
								title: d.user_agent,
								href: 'http://' + ip_address + '/',
								target: '_blank',
								textContent: ip_address,
							});
						}
					},
				}];
			},

			async exportExtens() {
				let data = await REST.GET('extensions');
				let controlTypes = new Map(Admin.HWPHONE_CONTROL_OPTIONS.map(o => [o.value, o.text]));

				await Export.xlsx({
					filename: 'extensions.xlsx',
					sheets: [{
						name: _('Linky'),
						columns: [{
							id: 'extension',
							name: _('Linka'),
							width: 10,
						}, {
							id: 'name',
							name: _('Název'),
							width: 30,
						}, {
							id: 'hwphone_control',
							name: _('Ovládání HW telefonu'),
							width: 25,
						}, {
							id: 'hwphone_autoanswer',
							name: _('Automatické vyzvednutí'),
							format: 'bool',
							width: 20,
						}, {
							id: 'devices',
							name: _('Připojená zařízení'),
							width: 45,
						}, {
							id: 'ip_addresses',
							name: _('IP adresy'),
							width: 35,
						}],
						rows: data.map(i => {
							return {
								extension: i.extension,
								name: i.name,
								hwphone_control: controlTypes.get(i.hwphone_control) || i.hwphone_control,
								hwphone_autoanswer: i.hwphone_autoanswer,
								devices: i.devices.map(d => d.user_agent).filter(v => v).join(', '),
								ip_addresses: i.devices.filter(d => d.reachable && d.user_agent && d.kind != 'webrtc' && d.ip_address)
									.map(d => d.ip_address.replace(/:.*$/, '')).join(', '),
							};
						}),
					}],
				});
			},
		}, this);
	},

	applyParams(...args) {
		return this.editor.applyParams(...args);
	},
});

// }}}

// Pauses:

// {{{ Admin.PausesScreen

GW.define('Admin.PausesScreen', 'Admin.Screen', {
	'after:initComponent'() {
		this.bodyEl.gwCreateChild({
			ref: 'editor',
			xtype: 'Widgets.EntityListEditor',
			title: _('Pauzy'),
			entityApiPrefix: 'pause-types',
			createText: _('Nová pauza'),
			selectText: _('Vyberte pauzu ze seznamu'),
			editText: _('Upravit pauzu'),
			deleteText: i => _('Opravdu smazat pauzu {name}?').replace('{name}', i.name),
			renderDetail: null,

			filterListItem(i, match) {
				return match([i.name, i.description || ''].join(' '));
			},

			getCreateFormFields() {
				return [{
					xtype: 'TextField',
					name: 'name',
					label: _('Název'),
				}, {
					xtype: 'TextareaField',
					name: 'description',
					label: _('Popis'),
					optional: true,
				}, {
					xtype: 'TextField',
					name: 'timeout',
					label: _('Délka pauzy v sekundách'),
					type: 'number',
					optional: true,
				}];
			},

			getEntityProperties() {
				return [{
					id: 'name',
					name: _('Název'),
					sortable: true,
				}, {
					id: 'description',
					name: _('Popis'),
					sortable: true,
				}, {
					id: 'timeout',
					name: _('Délka pauzy [s]'),
					sortable: true
				}];
			},
		}, this);
	},

	applyParams(...args) {
		return this.editor.applyParams(...args);
	},
});

// }}}

// External apps access (API):

// {{{ Admin.APITesterDialog

GW.define('Admin.APITesterDialog', 'Main.Dialog', {
	title: _('Tester zákaznického API'),

	'after:initComponent'() {
		this.el.classList.add('fullscreen');

		this.bodyEl.gwCreateChild({
			ref: 'tableEl',
			nodeName: 'table',
			className: 'admin-api-tester',
		}, this);

		this.load();
	},

	rangePath(base) {
		return [base, '?since=', {
			xtype: 'TextField',
			type: 'datetime-local',
			optional: true,
			getSubmitValue() {
				let v = this.getValue();
				if (v == null) {
					return v;
				}

				return Utils.formatUTCDateIso(new Date(v * 1000), true);
			},
		}, '&until=', {
			xtype: 'TextField',
			type: 'datetime-local',
			optional: true,
			getSubmitValue() {
				let v = this.getValue();
				if (v == null) {
					return v;
				}

				return Utils.formatUTCDateIso(new Date(v * 1000), true);
			},
		}];
	},

	//PLUGIN: extension point
	getEndpoints() {
		let me = this;

		function ts(off) {
			let d = new Date;

			d.setSeconds(d.getSeconds() + off);

			return Utils.formatUTCDateIso(d, true);
		}
		
		const groupSelectField = {
			xtype: 'SelectField',
			placeholder: _('Vyberte...'),
			popupAutoWidth: false,
			async getOptions({signal}) {
				let list = await me.callApi('GET', '/v1/user-groups');
				return list.map(i => ({
					value: i.id,
					popup_text: i.id + ' - ' + i.name,
					text: i.id,
				}));
			},
		}; 

		return [{
			group: 'call-originate',
		}, {
			method: 'POST',
			path: ['/v1/originate?wait_for_callee=', {
				xtype: 'SelectField',
				popupAutoWidth: false,
				placeholder: _('Vyberte...'),
				options: ['1', '0'].map(v => ({
					value: v,
					text: v,
				})),
				value: '1',
				valueOnClear: '1',
			}],
			async getDefaultBody() {
				let users = await REST.GET('users');

				users = users.filter(u => u.extension);

				return {
					caller: users[0] ? users[0].extension : '',
					callee: users[1] ? users[1].extension : '',
				};
			},
		}, {
			group: 'call-control',
		}, {
			method: 'POST',
			path: '/v1/call-control/hangup',
			async getDefaultBody() {
				let users = await REST.GET('users');

				users = users.filter(u => u.extension);

				return {
					exten: users[0] ? users[0].extension : '',
				};
			},
		}, {
			group: 'call-notification',
		}, {
			method: 'GET',
			path: '/v1/subscriptions',
		}, {
			method: 'POST',
			path: '/v1/subscriptions',
			async getDefaultBody() {
				let url = new URL(location.href);
				url.hash = '';
				url.search = '';
				url.pathname = '/api/debug/loopback';

				return {
					id: crypto.randomUUID(),
					callback_url: url,
					event_type: 'new-call',
					caller: 'external',
					callee: 'internal',
				};
			},
		}, {
			method: 'DELETE',
			path: ['/v1/subscriptions/', {
				xtype: 'SelectField',
				popupAutoWidth: false,
				placeholder: _('Vyberte...'),
				async getOptions({signal}) {
					let subs = await me.callApi('GET', '/v1/subscriptions');

					return subs.map(s => {
						return {
							value: s.id,
							popup_text: s.id + ' - ' + s.event_type + ' - ' + s.callback_url,
							text: s.id,
						};
					});
				},
			}],
		}, {
			group: 'contact-groups',
		}, {
			method: 'GET',
			path: '/v1/contact-directories',
		}, {
			method: 'PUT',
			path: ['/v1/contact-directories/', {
				xtype: 'SelectField',
				popupAutoWidth: false,
				placeholder: _('Vyberte...'),
				async getOptions({signal}) {
					let dirs = await me.callApi('GET', '/v1/contact-directories');

					return dirs.map(d => {
						return {
							value: d.id,
							popup_text: d.id + ' - ' + d.name,
							text: d.id,
						};
					});
				},
			}, '/contacts?mode=', {
				xtype: 'SelectField',
				popupAutoWidth: false,
				placeholder: _('Vyberte...'),
				options: ['append', 'replace'].map(v => ({
					value: v,
					text: v,
				})),
				value: 'replace',
				valueOnClear: 'replace',
			}],
			async getDefaultBody() {
				return [{
					name: 'Bob Tester',
					company: 'Bob Company',
					email: 'bob@tester',
					phones: ['111222333', '402'],
				}];
			},
		}, {
			group: 'contact-search',
		}, {
			method: 'GET',
			path: ['/v1/contacts?phone=', {
				xtype: 'TextField',
			}],
		}, {
			group: 'user-management',
		}, {
			method: 'GET',
			path: '/v1/user-groups',
		}, {
			method: 'GET',
			path: ['/v1/users?extension=', {
				xtype: 'TextField',
				optional: true,
			}],
		}, {
			method: 'POST',
			path: ['/v1/users/', {
				xtype: 'TextField',
			}, '/extension'],
			async getDefaultBody() {
				return {
					followme: null,
					extension: '401',
				};
			},
		}, {
			group: 'outbound-campaigns',
		}, {
			method: 'GET',
			path: '/v1/outbound/campaigns',
		}, {
			method: 'POST',
			path: '/v1/outbound/campaigns',
			async getDefaultBody() {
				let groups = await me.callApi('GET', '/v1/user-groups');

				return {
					//id: crypto.randomUUID(),
					name: 'Kampaň 1',
					description: 'Toto je kampaň',
					...(groups[0] ? {
						user_group: groups[0].id,
						prefix_queue: '9001',
					} : {}),
					start: ts(0),
					end: ts(60 * 60 * 24),
				};
			},
		}, {
			method: 'PUT',
			path: ['/v1/outbound/campaigns/', {
				xtype: 'SelectField',
				popupAutoWidth: false,
				placeholder: _('Vyberte...'),
				async getOptions({signal}) {
					let list = await me.callApi('GET', '/v1/outbound/campaigns');

					return list.map(i => {
						return {
							value: i.id,
							popup_text: i.id + ' - ' + i.name,
							text: i.id,
						};
					});
				},
			}],
			async getDefaultBody() {
				return {
					name: 'Kampaň 1 Edit',
					description: 'Toto je kampaň',
					start: ts(0),
					end: ts(60 * 60 * 24),
				};
			},
		}, {
			method: 'POST',
			path: ['/v1/outbound/campaigns/', {
				xtype: 'SelectField',
				placeholder: _('Vyberte...'),
				popupAutoWidth: false,
				async getOptions({signal}) {
					let list = await me.callApi('GET', '/v1/outbound/campaigns');

					return list.map(i => {
						return {
							value: i.id,
							popup_text: i.id + ' - ' + i.name,
							text: i.id,
						};
					});
				},
			}, '/cases'],
			async getDefaultBody() {
				return [{
					name: 'Pepa',
					company: 'Firma A',
					phone: '123456789',
					customer_ref: '123',
				}, {
					name: 'Lojza',
					phone: '123123123',
					customer_ref: '124',
				}, {
					phone: '555666777',
					customer_ref: '125',
					url: 'https://bronet.cz',
				}, {
					name: 'Jarda večer',
					phone: '123123123',
					time_ranges: [{
						start: '20:00',
						end: '23:00',
					}],
				}];
			},
		}, {
			method: 'GET',
			path: ['/v1/outbound/campaigns/', {
				xtype: 'SelectField',
				placeholder: _('Vyberte...'),
				popupAutoWidth: false,
				async getOptions({signal}) {
					let list = await me.callApi('GET', '/v1/outbound/campaigns');

					return list.map(i => {
						return {
							value: i.id,
							popup_text: i.id + ' - ' + i.name,
							text: i.id,
						};
					});
				},
			}, '/cases?activated_since=', {
				xtype: 'TextField',
				type: 'datetime-local',
				optional: true,
				getSubmitValue() {
					let v = this.getValue();
					if (v == null) {
						return v;
					}
					return Utils.formatUTCDateIso(new Date(v * 1000), true);
				},
			}, '&activated_until=', {
				xtype: 'TextField',
				type: 'datetime-local',
				optional: true,
				getSubmitValue() {
					let v = this.getValue();
					if (v == null) {
						return v;
					}
					return Utils.formatUTCDateIso(new Date(v * 1000), true);
				},
			}],
		}, {
			group: 'power-dialer-configs',
		}, {
			method: 'GET',
			path: '/v1/power-dialer-configs',
		}, {
			method: 'POST',
			path: '/v1/power-dialer-configs',
			async getDefaultBody() {
				return {
					//id: crypto.randomUUID(),
					name: 'Power dialer 1',
				};
			},
		}, {
			method: 'PUT',
			path: ['/v1/power-dialer-configs/', {
				xtype: 'SelectField',
				popupAutoWidth: false,
				placeholder: _('Vyberte...'),
				async getOptions({signal}) {
					let list = await me.callApi('GET', '/v1/power-dialer-configs');

					return list.map(i => {
						return {
							value: i.id,
							popup_text: i.id + ' - ' + i.name,
							text: i.id,
						};
					});
				},
			}],
			async getDefaultBody() {
				return {
					name: 'Power dialer 1 Edit',
				};
			},
		}, {
			group: 'cdr',
		}, {
			method: 'GET',
			path: ['/v1/cdr/calls?begin_since=', {
				xtype: 'TextField',
				type: 'datetime-local',
				optional: true,
				getSubmitValue() {
					let v = this.getValue();
					if (v == null) {
						return v;
					}

					return Utils.formatUTCDateIso(new Date(v * 1000), true);
				},
			}, '&id=', {
				xtype: 'TextField',
				optional: true,
			}],
		}, {
			group: 'reports',
		}, {
			method: 'GET',
			path: '/v1/pause-types',
		}, {
			method: 'GET',
			path: this.rangePath('/v1/reports/callbacks/agent-summary'),
		}, {
			method: 'GET',
			path: this.rangePath('/v1/reports/pauses/agent-summary'),
		}, {
			method: 'GET',
			path: this.rangePath('/v1/reports/activity/agent-queue-memberships'),
		}, {
			method: 'GET',
			path: this.rangePath('/v1/reports/activity/agent-presence'),
		}, {
			method: 'GET',
			path: this.rangePath('/v1/reports/activity/agent-pauses'),
		}, {
			group: 'agents',
		}, {
			method: 'GET',
			path: ['/v1/agents?user_email=', {
				xtype: 'TextField',
				optional: true,
			}],
		}, {
			method: 'POST',
			path: ['/v1/agents/changeset'],
			async getDefaultBody() {
				let agents = await me.callApi('GET', '/v1/agents');
				if (agents.length > 0) {
					return {
						changes: [{
							type: 'join-queues',
							agent_email: agents[0].user_email,
							queues: agents[0].queues.filter(q => !q.static).map(q => q.queue),
						}],
					};
				}

				return {
					changes: [],
				};
			},
		}, {
			group: 'planned-queue-members'
		}, {
			method: 'GET',
			path: ['/v1/planned-queue-members/planned?group_id=', groupSelectField],
		}, {
			method: 'GET',
			path: ['/v1/planned-queue-members/history?group_id=', groupSelectField],
		}, {
			method: 'POST',
			path: ['/v1/planned-queue-members?group_id=', groupSelectField],
			async getDefaultBody() {
				return {
					user_email: '',
					user_name: '',
					user_extension: '',
					queue: '',
					planned_ts: ts(60),
					action: 'join',
				};
			},
		}, {
			method: 'PUT',
			path: ['/v1/planned-queue-members/', {xtype: 'TextField'}, '?group_id=', groupSelectField],
			async getDefaultBody() {
				return {
					user_email: '',
					user_name: '',
					user_extension: '',
					queue: '',
					planned_ts: ts(60),
					action: 'join',
				};
			},
		}, {
			method: 'DELETE',
			path: ['/v1/planned-queue-members/', {xtype: 'TextField'}, '?group_id=', groupSelectField],
		}];
	},

	async callApi(method, path, data, el) {
		let url = new URL('/customer-api' + path, location.href);
		url.hash = '';

		let res = await fetch(url, {
			method,
			mode: 'same-origin',
			credentials: 'omit',
			cache: 'no-store',
			redirect: 'error',
			referrerPolicy: 'no-referrer',
			body: data,
			headers: {
				Authorization: 'Basic ' + btoa(this.client.id + ':' + this.client.secret),
				'Content-Type': 'application/json',
				'Accept': 'application/json',
			},
		});

		let ct = res.headers.get('Content-Type') || '';
		let body, res_data;
		if (ct.match(/^application\/json/)) {
			res_data = await res.json();
			body = JSON.stringify(res_data, null, '    ');
		} else if (!ct.match(/^(image|video|application)/)) {
			body = await res.text();
		}

		if (el) {
			el.dataset.status = res.ok ? 'ok' : 'fail';
			el.textContent = 'Status: ' + res.status + ' ' + res.statusText + '\n' + (typeof body !== 'undefined' ? 
				('Response:\n' + body) : 
				('Headers:\n\n' + [...res.headers.entries()].map(([key, value]) => `${key}: ${value}`).join('\n'))
			);
		}

		if (!el && !res.ok) {
			throw new Error(_('Volání API selhalo') + ': ' + res.statusText);
		}

		return res_data;
	},

	async renderEndpoint(table, ep) {
		let data = '';

		if (ep.getDefaultBody) {
			try {
				data = await ep.getDefaultBody();
			} catch (ex) {
				console.log(ex);
			}
		}

		let pathEls = [];
		let get_path = () => ep.path;
		let clear_path = function() {};

		pathEls.push({
			nodeName: 'span',
			className: 'method',
			textContent: ep.method,
			'attr:data-method': ep.method,
		});

		if (typeof ep.path == 'string') {
			pathEls.push(ep.path);
		} else {
			for (let p of ep.path) {
				if (typeof p == 'string') {
					pathEls.push(p);
				} else {
					pathEls.push(GW.create(p));
				}
			}

			get_path = () => {
				let res = '';
				for (let p of pathEls.slice(1)) {
					if (typeof p == 'string') {
						res += p;
					} else {
						let v = p.getSubmitValue ? p.getSubmitValue() : p.getValue();
						if (!v && !p.optional) {
							throw new Error(_('Vyberte hodnotu'));
						}

						if (v != null) {
							res += encodeURIComponent(v);
						}
					}
				}
				return res;
			};

			clear_path = () => {
				for (let p of pathEls.slice(1)) {
					if (typeof p != 'string') {
						p.setValue(p.valueOnClear || null);
					}
				}
			};
		}

		let hdr = table.gwCreateChild({
			nodeName: 'tr',
			className: 'header',
			children: [{
				nodeName: 'td',
				colSpan: '3',
				children: [{
					className: 'flex',
					children: [{
						className: 'expander',
						children: [Utils.useIcon('arrow-simple-down')],
						'on:click': ev => toggle(ev.target.closest('.expander')),
					}, {
						className: 'title',
						children: pathEls,
					}, {
						className: 'fill',
					}, {
						ref: '$send',
						nodeName: 'button',
						className: 'primary hidden',
						textContent: _('Odeslat'),
						'on:click': async ev => {
							let body = tr.bodyInput ? tr.bodyInput.value : undefined;

							await this.callApi(ep.method, get_path(), body, tr.resultEl);
						},
					}],
				}],
			}],
		});

		function toggle(ex) {
			let hidden = !tr.classList.contains('hidden');

			tr.gwHide(hidden);
			ex.gwReplaceChildren([Utils.useIcon(hidden ? 'arrow-simple-down' : 'arrow-simple-up')]);
			hdr.$send.gwHide(hidden);
		}

		let tr = table.gwCreateChild({
			className: 'hidden',
			nodeName: 'tr',
			children: [{
				nodeName: 'td',
				className: 'req',
				children: [{
					':skip': !['POST', 'PUT'].includes(ep.method),
					ref: 'bodyInput',
					nodeName: 'textarea',
					value: typeof data == 'string' ? data : JSON.stringify(data, null, '    '),
				}],
			}, {
				nodeName: 'td',
				className: 'arrow',
				children: [Utils.useIcon('arrow-right')],
			}, {
				ref: 'resultEl',
				className: 'res',
				nodeName: 'td',
			}],
		});
	},

	async load() {
		for (let ep of this.getEndpoints()) {
			if (ep.group) {
				let title = this.apiMap[ep.group] || ep.group;

				this.tableEl.gwCreateChild({
					nodeName: 'tr',
					className: 'group-header',
					children: [{
						nodeName: 'td',
						colSpan: '3',
						textContent: title,
					}],
				});

				continue;
			}

			await this.renderEndpoint(this.tableEl, ep);
		}
	},
});

// }}}
// {{{ Admin.UserCondsScreen

GW.define('Admin.UserCondsScreen', 'Admin.Screen', {

	'after:initComponent'() {
		this.bodyEl.gwReplaceChildren([{
			ref: 'list',
			xtype: 'Widgets.EditableItemSelector',
			emptyListText: _('Nemáte žádné časové podmínky'),
			addButtonText: _('Přidat podmínku'),
			createTitle: _('Nová časová podmínka'),
			editTitle: _('Upravit časovou podmínku'),
			deleteTitle: _('Opravdu chcete smazat časovou podmínku "{name}"?'),
			itemApiPath: 'user-conditions',
			itemQueryParam: 'usercond',

			getItemFormFields() {
				return [{
					xtype: 'TextField',
					name: 'name',
					label: _('Název'),
				}, {
					xtype: 'TextareaField',
					name: 'description',
					label: _('Popis'),
					optional: true,
				}];
			},

			getItemMenuOptions(item) {
				return [{
					iconName: 'pencil',
					text: _('Upravit'),
					handle: this.editItem.bind(this, item),
				}, {
					iconName: 'cross',
					hidden: item.used,
					text: _('Smazat'),
					handle: this.deleteItem.bind(this, item),
				}];
			},

			allowAction(item, action) {
				if (item && item.used && action == 'delete') {
					return false;
				}
				return true;
			},
			
			'on:itemcreate': (c, item) => {
				Application.route({
					paramsFn: () => new URLSearchParams({item: item.id}),
				});
			},
			'on:itemupdate': (c, item) => {
				this.reload();
			},
			'on:itemdelete': (c, item) => {
				this.reload();
			},
		}, {
			ref: 'cards',
			xtype: 'Widgets.CardSwitcher',
			cls: 'panel-container',
		}], this);
	},

	reload() {
		this.applyParams(new URLSearchParams(location.search));
	},

	async applyParams(params) {
		let list = this.list;
		let {item, items} = await this.list.applyParams(params);

		if (!item) {
			this.cards.show({
				id: 'empty',
				xtype: 'GW.Component',
				initComponent() {
					this.el = document.gwCreateElement({
						className: 'empty',
						children: [{
							nodeName: 'img',
							src: APP_BASE + 'img/empty-boxes.svg',
							className: 'illustration',
						}, {
							nodeName: 'h5',
							textContent: this.emptyText,
						}, {
							':skip': !list.allowCreate,
							nodeName: 'button',
							type: 'button',
							className: 'primary icon-left',
							children: [Utils.useIcon('plus'), list.addButtonText],
							'on:click': ev => list.createItem(),
						}],
					});
				},
			})
			return;
		}

		let card = await this.cards.show({
			id: 'detail',
			xtype: 'Widgets.TimeConditionEditor',
			
			'after:initComponent'() {
				this.el.prepend(document.gwCreateElement({
					className: 'used-note',
					ref: 'noteEl',
				}, this));
			},

			async saveDataApi(id, data) {
				await REST.PUT('user-conditions/' + id, data);
			},

			async getDataApi(id, {signal}) {
				return await REST.GET(`user-conditions/${id}`, null, {signal});
			},
		});

		if (card.loadData) {
			card.noteEl.textContent = item.used ?
				_('Tato časová podmínka je použita minimálně v jednom call flow') :
				_('Tato časová podmínka není použita');
			await card.loadData(item);
		}
	},
});

// }}}
// {{{ Admin.ApiClients

GW.define('Admin.ApiClients', 'Admin.Screen', {
	'after:initComponent'() {
		let me = this;

		this.bodyEl.gwCreateChild({
			ref: 'editor',
			xtype: 'Widgets.EntityListEditor',
			title: _('API přístupy'),
			entityApiPrefix: 'api-clients',
			createText: _('Přidat aplikaci'),
			selectText: _('Vyberte položku ze seznamu'),
			editText: _('Upravit aplikaci'),
			deleteText: i => _('Opravdu smazat aplikaci {name}?').replace('{name}', i.name),

			apiOptions: [],
			apiMap: {},

			filterListItem(i, match) {
				return match([i.name, i.id || '', ...i.access.allowed_api.map(a => this.apiMap[a] || a)].join(' '));
			},

			getExtraDetailActions(item) {
				return [{
					iconName: 'bug',
					text: _('API Tester'),
					handle: () => {
						new Admin.APITesterDialog({
							apiMap: this.apiMap,
							client: item,
						});
					},
				}];
			},

			getCreateFormFields() {
				return [{
					xtype: 'TextField',
					name: 'name',
					label: _('Název aplikace'),
				}, {
					xtype: 'TextareaField',
					name: 'description',
					label: _('Popis'),
					optional: true,
				}, {
					xtype: 'CheckboxGroup',
					name: 'allowed_api',
					label: _('Povolená API'),
					options: this.apiOptions,
					value: [],
				}];
			},

			getEditDialogData(item) {
				item.allowed_api = item.access.allowed_api;
				return item;
			},

			getEntityProperties() {
				let me = this;
				let docs = this.getAPIDocs();

				return [{
					id: 'name',
					name: _('Název aplikace'),
					sortable: true,
				}, {
					id: 'description',
					name: _('Popis'),
					detailOnly: true,
				}, {
					id: 'id',
					name: _('ID klienta'),
					sortable: true,
				}, {
					detailOnly: true,
					id: 'secret',
					name: _('Tajný klíč'),
				}, {
					id: 'allowed_api',
					name: _('Povolená API'),
					sortable: {
						type: 'string',
						value: 'celltext',
					},
					formatCell(td, v, row) {
						td.textContent = row.access.allowed_api.map(a => me.apiMap[a] || a).join(', ');
					},
				}, {
					detailOnly: true,
					id: 'docs',
					name: _('Dokumentace API'),
					formatCell(td, v, row) {
						for (let d of docs) {
							td.gwCreateChild({
								nodeName: 'p',
								children: [{
									nodeName: 'a',
									textContent: d.title,
									target: '_blank',
									href: d.path,
								}],
							});
						}
					},
				}];
			},

			getAPIDocs() {
				return me.getAPIDocs();
			},

			async preloadData() {
				let signal = Utils.abortAndRetry(this, 'reload-options');
				let [list] = await Utils.cache(this, 'options-list', 60000, () => REST.GET('customer-api-options'), signal);

				this.apiOptions = list.map(v => {
					return {
						value: v.id,
						text: v.name,
					};
				});

				this.apiMap = Object.fromEntries(this.apiOptions.map(s => [s.value, s.text]));
			},
		}, this);
	},

	//PLUGIN: extension point
	getAPIDocs() {
		return [{
			title: _('Základní API'),
			path: '/docs/customer-api/bccp22-api.pdf',
		}];
	},

	applyParams(...args) {
		return this.editor.applyParams(...args);
	},
});

// }}}

// Disk usage:

// {{{ Admin.DiskUsageScreen
	
GW.define('Admin.DiskUsageScreen', 'Admin.Screen', {

	'after:initComponent'() {
		this.bodyEl.classList.add('disk-usage');
		this.bodyEl.gwReplaceChildren([{
			className: 'disk-usage-loading',
			textContent: _('Načítám...'),
		}], this);
	},

	async applyParams() {
		const data = await REST.GET('/api/disk-usage');
		
		this.bodyEl.gwReplaceChildren(data.devs.map(dev => {
			const used = dev.kb_used * 1024;
			const total = dev.kb_total * 1024;
			return {
				className: 'disk-usage-disk',
				children: [{
					className: 'disk-usage-header',
					children: [{
						className: 'disk-usage-title',
						textContent: dev.dev,
						title: dev.dev,
					}, {
						className: 'disk-usage-label',
						textContent: _('Volných {percent}')
							.replace('{percent}', Formats.percent( (total - used) / total)),
					}]
				}, {
					className: 'disk-usage-progress',
					children: [{
						style: 'width: ' + (used / total * 100) + '%',
					}],
				}, {
					className: 'disk-usage-footer',
					children: [{
						textContent: Formats.size(used),
					}, {
						textContent: Formats.size(total),
					}],
				}],
			};
		}));

	}

})

// }}}

// Screens/routing:

// {{{ Screens list

/*
 * PLUGIN: extension point for adding more screens
 */
Admin.screens = [{
	id: 'users',
	icon: 'user',
	title: _('Uživatelé'),
	xtype: 'Admin.UsersScreen',
	superadmin_only: true,
}, {
	id: 'groups',
	icon: 'users',
	title: _('Skupiny'),
	xtype: 'Admin.UserGroupsScreen',
}, {
	id: 'extens',
	icon: 'phone',
	title: _('Linky'),
	xtype: 'Admin.ExtensionsScreen',
	superadmin_only: true,
}, {
	id: 'queues',
	icon: 'queue',
	title: _('Fronty'),
	xtype: 'Admin.QueuesScreen',
	superadmin_only: true,
}, {
	id: 'pauses',
	icon: 'pause',
	title: _('Pauzy'),
	xtype: 'Admin.PausesScreen',
	superadmin_only: true,
}, {
	id: 'mailboxes',
	icon: 'voicemail',
	title: _('Záznamíky'),
	xtype: 'Admin.MailboxesScreen',
	superadmin_only: true,
}, {
	id: 'speedials',
	icon: 'dashboard',
	title: _('Rychlé volby'),
	xtype: 'Admin.SpeeddialsScreen',
	superadmin_only: true,
}, {
	id: 'contacts',
	icon: 'book',
	title: _('Adresáře kontaktů'),
	xtype: 'Admin.ContactsScreen',
	superadmin_only: true,
}, {
	id: 'cathierarchy',
	icon: 'book',
	title: _('Kategorie hovorů'),
	xtype: 'Admin.CallCategoryHierarchies',
	superadmin_only: true,
}, {
	id: 'userconds',
	icon: 'calendar',
	title: _('Časové podmínky'),
	xtype: 'Admin.UserCondsScreen',
	superadmin_only: true,
}, {
	id: 'apiclients',
	icon: 'play',
	title: _('API přístupy'),
	xtype: 'Admin.ApiClients',
	superadmin_only: true,
},  {
	id: 'diskusage',
	icon: 'harddrive',
	title: _('Obsazení disků'),
	xtype: 'Admin.DiskUsageScreen',
	superadmin_only: true,
}];

// }}}
// {{{ Routes

Application.addRoutes([{
	group: 'auth',
	module: 'admin',
	path: APP_BASE + 'admin',
	async handler({params, captures}) {
		if (Application.isSuperadminUser()) {
			Application.replaceRoute(APP_BASE + 'admin/users');
		} else {
			Application.replaceRoute(APP_BASE + 'admin/groups');
		}
	}    
}]);

Application.addRoutes(Admin.screens.map(s => {
	return {
		group: 'auth',
		module: 'admin',
		path: APP_BASE + 'admin/' + s.id,
		async handler({params, captures}) {
			if (!Application.isSuperadminUser() && s.superadmin_only) {
				Application.replaceRoute(APP_BASE + 'admin/groups');
				return;
			}

			let c = Application.show({
				id: s.xtype,
				xtype: s.xtype,
			});

			c.applyParams(params);
		},
	};
}));

// }}}

Application.registerModule({
	id: 'admin',
	title: _('Administrace'),
	tagline: _('Správa uživatelů, skupin a další nastavení systému'),
	icon: 'settings',
	href: APP_BASE + 'admin',
});
